import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MapContainer, Polyline, Circle } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faChevronDown,
  faChevronUp,
  faBuilding,
  faTruck,
  faLandmark,
  faGasPump,
  faMapPin,
  faChevronLeft,
  faChevronRight,
  faSatelliteDish,
  faUser,
  faLocationCrosshairs,
} from '@fortawesome/free-solid-svg-icons'
import {
  featureCentroid,
  fetchTigerwebBoundary,
  fetchTigerwebBoundaries,
  isTigerwebBoundaryLevel,
  searchTigerwebJurisdictions,
  type TigerwebBoundaryLevel,
  type TigerwebFeatureCollection,
  type TigerwebSearchHit,
} from '../../services/tigerwebBoundaryService'
import type {
  ControlCenterAsset,
  ControlCenterMunicipality,
  ControlCenterJob,
  ControlCenterPerson,
} from '../../types/controlCenter'
import { API_BASE_URL } from '../../utils/apiBaseUrl'
import { apiFetch } from '../../utils/apiFetch'
import {
  fleetEntityToControlCenterAsset,
  matchAssetImageUrls,
} from '../../utils/controlCenterLinxupAssets'
import {
  EXTERNAL_MUNICIPALITY_COLOR,
  censusMunicipalityId,
  licenseTypeLabel,
  mapMunicipalityLicenseRow,
  municipalityFromCensusHit,
  STATE_ABBR_TO_FIPS,
  stateAbbrFromCensusHit,
} from '../../utils/controlCenterMunicipalityLicenses'
import { mapJobtreadJobRow } from '../../utils/controlCenterJobtreadJobs'
import { mapJobtreadPersonRow, enrichPeopleWithJobLocations } from '../../utils/controlCenterJobtreadPeople'
import { normalizeLatLng } from '../../utils/geoCoordinates'
import {
  buildFleetIdLabelMap,
  type TallyGroupLabelApiRow,
} from '../../utils/tallyFleetGroupLabelMaps'
import {
  buildHiddenFleetSet,
  deriveFleetIssues,
  normalizeFleetEntities,
  type FleetIssue,
} from '../../utils/tallyFleetEntities'
import {
  DEFAULT_OPERATION_INPUTS,
  entityRef,
  getAvailableOperators,
  resolveStepPrimary,
  createEmptyOperationStep,
  clearOperationResultsForParentEntities,
  clearSelectionBackedOperationSteps,
  clearStaleSelectionOperationResults,
  operatorLabel,
  formatOperationStepsShareText,
  runEtaOperation,
  runLocalLicenseCheckOperation,
  runNearbyPlacesOperation,
  runWeatherForecastOperation,
  runTimeOperation,
  runCertCheckOperation,
  runOperation,
  OPERATORS,
  isNearbyLayerTarget,
  operationEntityFromJob,
  operationEntityFromPerson,
  operationEntityFromPersonIdentity,
  operationEntityFromAsset,
  operationEntityFromMunicipality,
  operationEntityFromMapPoint,
  findStepMapPoint,
  type OperationEntity,
  type OperationInputs,
  type OperationMapPoint,
  type OperationResult,
  type OperationStep,
  type OperationStepInputSource,
  type OperatorDef,
  type OperatorId,
} from './operations'
import {
  fetchRainViewerRadarFrames,
  type RainViewerRadarFrame,
} from './rainViewerWeather'
import { RainViewerRadarLayer } from './RainViewerRadarLayer'

import type {
  BoundaryOverlayToggles,
  EntityCategory,
  MapMarkerVisibility,
  MapStyleId,
  MappableEntity,
  OmniCategory,
  OmniSearchHit,
  OmniSelection,
} from './map/controlCenterMapTypes'
import { DEFAULT_MAP_MARKER_VISIBILITY } from './map/controlCenterMapTypes'
import {
  MAP_STYLE_STORAGE_KEY,
  OUTPUT_PANEL_STORAGE_KEY,
  isMapStyleId,
  getInitialMapStyle,
  getInitialOutputOpen,
} from './map/mapStyles'
import {
  ASSET_KIND_COLOR,
  MUNICIPALITY_LEVEL_COLOR,
  BOUNDARY_OVERLAY_LEVELS,
  EMPTY_BOUNDARY_OVERLAYS,
  assetKindLabel,
  copyTextToClipboard,
  boundaryFitMaxZoom,
  boundaryOverlaysFromShowLevel,
  jobStatusColor,
  municipalityLicenseIsExpired,
  municipalityLicenseIsExpiringSoon,
  personHasMapPin,
  personKindLabel,
  personMarkerPosition,
  PERSON_KIND_COLOR,
  showLevelFromBoundaryOverlays,
  titleCase,
} from './map/mapEntityPresentation'
import {
  OMNI_GROUP_LABEL,
  OMNI_RESULT_CAP_PER_GROUP,
  assetDriverName,
  assetOmniHaystack,
  jobOmniHaystack,
  municipalityOmniHaystack,
  omniNamesLooselyMatch,
  omniQueryMatches,
  personOmniHaystack,
} from './map/omniSearch'
import {
  hasCoincidentNeighbor,
  type ClusterableMarker,
} from './map/markerClustering'
import { MapClusterLayer } from './map/MapClusterLayer'
import {
  areOperatorArgsComplete,
  destinationOptionsFromStepResults,
  destinationTargetsStepResult,
} from './map/mapOperatorArgs'

import { PanelToggleChip } from './map/MapChrome'
import {
  ControlCenterMapBasemapLayer,
  TrackUserCameraGestures,
  InvalidateMapSize,
  FlyToSelectedEntity,
  FocusOperationResultPoint,
  MunicipalityBoundaryLayer,
  StopMapOnSelectionChange,
  FitOperationResultBounds,
} from './map/mapLeafletHelpers'

import { operatorDocksPopupToCorner } from './map/MapEntityPopups'
import {
  EntityMapMarker as EntityMapMarkerModule,
} from './map/EntityMapMarker'
import { UnlocatedEntityDockedPopup } from './map/UnlocatedEntityDockedPopup'
import {
  OperationResultMarker as OperationResultMarkerModule,
} from './map/OperationResultMarker'
import { EntityDragGhost } from './map/useEntityDrag'
import { ControlCenterMapToolbar } from './map/ControlCenterMapToolbar'
import { MapLoadingOverlay } from './map/MapLoadingOverlay'
import { OutputPanel } from './map/OutputPanel'
import useMediaQuery from '../../hooks/useMediaQuery'
import { useControlCenterOperationConfig } from '../../hooks/useControlCenterOperationConfig'
import { useControlCenterFilterPreset } from '../../hooks/useControlCenterFilterPreset'
import { useKioskRole } from '../../hooks/useKioskRole'
import { useControlCenterKioskSync } from './useControlCenterKioskSync'
import {
  EMPTY_ASSET_FILTERS,
  EMPTY_JOB_FILTERS,
  EMPTY_MUNICIPALITY_FILTERS,
  EMPTY_PERSON_FILTERS,
  normalizeFilterPreset,
} from '../../utils/controlCenterFilterPreset'

const USER_LOCATION_POINT_ID = 'user-location'
const USER_LOCATION_COLOR = '#0284c7'

const ControlCenterMap: React.FC<{ active?: boolean }> = ({ active = true }) => {
  const mapRef = useRef<L.Map | null>(null)
  const isDesktop = useMediaQuery('(min-width: 1024px) and (orientation: landscape)')
  const isWideDesktop = useMediaQuery('(min-width: 1280px) and (orientation: landscape)')
  const isMobile = !isDesktop
  const { config: operationConfig } = useControlCenterOperationConfig()
  const { config: filterPreset, loading: filterPresetLoading } = useControlCenterFilterPreset()
  const kioskRole = useKioskRole()
  const skipFilterPreset = kioskRole === 'display'
  const appliedFilterPresetRef = useRef(false)
  const [mapStyleId, setMapStyleId] = useState<MapStyleId>(() => getInitialMapStyle())
  const [outputOpen, setOutputOpen] = useState<boolean>(() => getInitialOutputOpen())
  // Keep zoom/map-type/weather inline only when there's room beside an open output panel.
  const inlineMapTools = isWideDesktop || (isDesktop && !outputOpen)
  const outputOpenRef = useRef(outputOpen)
  outputOpenRef.current = outputOpen
  const [outputBadgeFlashToken, setOutputBadgeFlashToken] = useState(0)
  const [focusId, setFocusId] = useState<string | null>(null)
  const [omniSelections, setOmniSelections] = useState<OmniSelection[]>([])
  const [omniQuery, setOmniQuery] = useState('')
  const [omniOpen, setOmniOpen] = useState(false)
  const [omniFiltersOpen, setOmniFiltersOpen] = useState(false)
  const [omniActiveFilterTypes, setOmniActiveFilterTypes] = useState<OmniCategory[]>([])
  const [boundaryGeojson, setBoundaryGeojson] = useState<TigerwebFeatureCollection | null>(null)
  const [boundaryForId, setBoundaryForId] = useState<string | null>(null)
  const [boundaryLoading, setBoundaryLoading] = useState(false)
  const initialStepRef = useRef<OperationStep | null>(null)
  if (!initialStepRef.current) initialStepRef.current = createEmptyOperationStep()
  const [operationSteps, setOperationSteps] = useState<OperationStep[]>(() => [
    initialStepRef.current!,
  ])
  const [activeStepId, setActiveStepId] = useState<string>(() => initialStepRef.current!.id)
  const [operationRunning, setOperationRunning] = useState(false)
  const operationRunGenRef = useRef(0)
  const [shareCopied, setShareCopied] = useState(false)
  const shareCopiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [focusedResultPointId, setFocusedResultPointId] = useState<string | null>(null)
  const [resultFocusToken, setResultFocusToken] = useState(0)
  const [entityFocusToken, setEntityFocusToken] = useState(0)
  const [userLocationPoint, setUserLocationPoint] = useState<OperationMapPoint | null>(null)
  const [locatingMe, setLocatingMe] = useState(false)
  const [locateError, setLocateError] = useState<string | null>(null)
  const [weatherEnabled, setWeatherEnabled] = useState(false)
  const [weatherFrames, setWeatherFrames] = useState<RainViewerRadarFrame[]>([])
  const boundaryRequestIdRef = useRef(0)

  const [jobtreadJobs, setJobtreadJobs] = useState<ControlCenterJob[]>([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [, setJobsError] = useState<string | null>(null)

  const [jobtreadPeople, setJobtreadPeople] = useState<ControlCenterPerson[]>([])
  const [peopleLoading, setPeopleLoading] = useState(true)
  const [, setPeopleError] = useState<string | null>(null)

  const [linxupAssets, setLinxupAssets] = useState<ControlCenterAsset[]>([])
  const [assetsLoading, setAssetsLoading] = useState(true)
  const [, setAssetsError] = useState<string | null>(null)
  const [fleetGroupLabels, setFleetGroupLabels] = useState<Map<number, string>>(() => new Map())
  const [hiddenFleetIds, setHiddenFleetIds] = useState<Set<number>>(() => new Set())
  const [assetImageRows, setAssetImageRows] = useState<
    Array<{
      imageUrls?: string[]
      image?: string
      img?: string
      serialNumber?: string
      externalIdentifiers?: {
        vin?: string
        serialNumber?: string
        imei?: string
        deviceUUID?: string
      }
    }>
  >([])
  const [assetImagesLoading, setAssetImagesLoading] = useState(false)
  const [assetGalleryIdx, setAssetGalleryIdx] = useState(0)

  const [jobFilters, setJobFilters] = useState(() =>
    skipFilterPreset ? EMPTY_JOB_FILTERS : filterPreset.jobFilters,
  )
  const [personFilters, setPersonFilters] = useState(() =>
    skipFilterPreset ? EMPTY_PERSON_FILTERS : filterPreset.personFilters,
  )
  const [assetFilters, setAssetFilters] = useState(() =>
    skipFilterPreset ? EMPTY_ASSET_FILTERS : filterPreset.assetFilters,
  )
  const [municipalityFilters, setMunicipalityFilters] = useState(() =>
    skipFilterPreset ? EMPTY_MUNICIPALITY_FILTERS : filterPreset.municipalityFilters,
  )
  const [boundaryOverlays, setBoundaryOverlays] = useState<BoundaryOverlayToggles>(() =>
    skipFilterPreset
      ? EMPTY_BOUNDARY_OVERLAYS
      : boundaryOverlaysFromShowLevel(filterPreset.boundaryShowLevel),
  )
  const [boundaryOverlayGeojson, setBoundaryOverlayGeojson] = useState<
    Record<TigerwebBoundaryLevel, TigerwebFeatureCollection | null>
  >({ state: null, county: null, city: null })
  const [boundaryOverlaysLoading, setBoundaryOverlaysLoading] = useState(false)
  const [mapMarkerVisibility, setMapMarkerVisibility] = useState<MapMarkerVisibility>(() =>
    skipFilterPreset
      ? DEFAULT_MAP_MARKER_VISIBILITY
      : {
          ...DEFAULT_MAP_MARKER_VISIBILITY,
          jobs: filterPreset.showAllJobs,
          people: filterPreset.showAllPeople,
          assets: filterPreset.showAllAssets,
        },
  )
  const peopleWithJobLocations = useMemo(
    () => enrichPeopleWithJobLocations(jobtreadPeople, jobtreadJobs),
    [jobtreadPeople, jobtreadJobs],
  )

  const [municipalityLicenses, setMunicipalityLicenses] = useState<ControlCenterMunicipality[]>([])
  const [municipalitiesLoading, setMunicipalitiesLoading] = useState(true)
  const [, setMunicipalitiesError] = useState<string | null>(null)
  const [omniBootstrapComplete, setOmniBootstrapComplete] = useState(false)
  const [externalMunicipality, setExternalMunicipality] = useState<ControlCenterMunicipality | null>(
    null,
  )
  const [externalMunicipalitiesById, setExternalMunicipalitiesById] = useState<
    Record<string, ControlCenterMunicipality>
  >({})
  const [censusPlaceResolvingId, setCensusPlaceResolvingId] = useState<string | null>(null)
  const [omniCensusHits, setOmniCensusHits] = useState<TigerwebSearchHit[]>([])
  const [omniCensusSearching, setOmniCensusSearching] = useState(false)

  const kioskSnapshotJson = useMemo(
    () =>
      JSON.stringify({
        mapCenter: null,
        mapZoom: null,
        mapStyleId,
        weatherEnabled,
        omniSelections,
        focusId,
        focusedResultPointId,
        entityFocusToken,
        resultFocusToken,
        userLocationPoint,
        operationSteps,
        activeStepId,
        outputOpen,
        externalMunicipality,
        externalMunicipalitiesById,
        mapMarkerVisibility,
        boundaryOverlays,
        jobFilters,
        personFilters,
        assetFilters,
        municipalityFilters,
      }),
    [
      mapStyleId,
      weatherEnabled,
      omniSelections,
      focusId,
      focusedResultPointId,
      entityFocusToken,
      resultFocusToken,
      userLocationPoint,
      operationSteps,
      activeStepId,
      outputOpen,
      externalMunicipality,
      externalMunicipalitiesById,
      mapMarkerVisibility,
      boundaryOverlays,
      jobFilters,
      personFilters,
      assetFilters,
      municipalityFilters,
    ],
  )

  const kioskApply = useMemo(
    () => ({
      setMapStyleId,
      setWeatherEnabled,
      setOmniSelections,
      setFocusId,
      setFocusedResultPointId,
      setEntityFocusToken,
      setResultFocusToken,
      setUserLocationPoint,
      setOperationSteps,
      setActiveStepId,
      setOutputOpen,
      setExternalMunicipality,
      setExternalMunicipalitiesById,
      setMapMarkerVisibility,
      setBoundaryOverlays,
      setJobFilters,
      setPersonFilters,
      setAssetFilters,
      setMunicipalityFilters,
    }),
    [],
  )

  const { isDisplayKiosk } = useControlCenterKioskSync({
    active,
    mapRef,
    snapshotJson: kioskSnapshotJson,
    apply: kioskApply,
  })

  useEffect(() => {
    if (isDisplayKiosk || skipFilterPreset) return
    if (filterPresetLoading) return
    if (appliedFilterPresetRef.current) return
    appliedFilterPresetRef.current = true
    const next = normalizeFilterPreset(filterPreset)
    setJobFilters(next.jobFilters)
    setPersonFilters(next.personFilters)
    setAssetFilters(next.assetFilters)
    setMunicipalityFilters(next.municipalityFilters)
    setMapMarkerVisibility((prev) => ({
      ...prev,
      jobs: next.showAllJobs,
      people: next.showAllPeople,
      assets: next.showAllAssets,
    }))
    setBoundaryOverlays(boundaryOverlaysFromShowLevel(next.boundaryShowLevel))
  }, [isDisplayKiosk, skipFilterPreset, filterPresetLoading, filterPreset])

  useEffect(() => {
    if (isDisplayKiosk) setOutputOpen(true)
  }, [isDisplayKiosk])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setJobsError(null)
        setJobsLoading(true)
        const token = localStorage.getItem('lts_jwt_token')
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }
        const res = await apiFetch(`${API_BASE_URL}/capabilities/execute`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            app: 'controlCenter',
            capability: 'jobtread.listJobsWithLocations',
            input: {},
          }),
        })
        const payload = await res.json().catch(() => ({}))
        if (!res.ok || !payload?.success) {
          throw new Error(payload?.message || `Failed to load jobs (${res.status})`)
        }
        const rows = Array.isArray(payload?.data?.jobs) ? payload.data.jobs : []
        if (!cancelled) {
          setJobtreadJobs(
            rows
              .map((row: Record<string, unknown>) => mapJobtreadJobRow(row))
              .filter((job: ControlCenterJob | null): job is ControlCenterJob => job != null),
          )
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setJobsError(e instanceof Error ? e.message : 'Failed to load jobs')
        }
      } finally {
        if (!cancelled) setJobsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const fetchJobtreadPeople = useCallback(async () => {
    const token = localStorage.getItem('lts_jwt_token')
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
    const res = await apiFetch(`${API_BASE_URL}/capabilities/execute`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        app: 'controlCenter',
        capability: 'jobtread.listPeople',
        input: {},
      }),
    })
    const payload = await res.json().catch(() => ({}))
    if (!res.ok || !payload?.success) {
      throw new Error(payload?.message || `Failed to load people (${res.status})`)
    }
    const rows = Array.isArray(payload?.data?.people) ? payload.data.people : []
    return rows
      .map((row: Record<string, unknown>) => mapJobtreadPersonRow(row))
      .filter((person: ControlCenterPerson | null): person is ControlCenterPerson => person != null)
  }, [])

  const peopleSectionEnabled = active

  useEffect(() => {
    if (!peopleSectionEnabled) {
      setPeopleLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setPeopleError(null)
        setPeopleLoading(true)
        const next = await fetchJobtreadPeople()
        if (!cancelled) setJobtreadPeople(next)
      } catch (e: unknown) {
        if (!cancelled) {
          setPeopleError(e instanceof Error ? e.message : 'Failed to load people')
        }
      } finally {
        if (!cancelled) setPeopleLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [peopleSectionEnabled, fetchJobtreadPeople])

  useEffect(() => {
    if (!peopleSectionEnabled) return
    const id = window.setInterval(() => {
      fetchJobtreadPeople()
        .then((next) => setJobtreadPeople(next))
        .catch(() => {
          /* keep last good data */
        })
    }, 60_000)
    return () => window.clearInterval(id)
  }, [peopleSectionEnabled, fetchJobtreadPeople])

  useEffect(() => {
    if (!active) {
      setMunicipalitiesLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setMunicipalitiesError(null)
        setMunicipalitiesLoading(true)
        const token = localStorage.getItem('lts_jwt_token')
        const headers: Record<string, string> = {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }
        const res = await apiFetch(`${API_BASE_URL}/municipality-licenses`, { headers })
        const payload = await res.json().catch(() => ({}))
        if (!res.ok || !payload?.success) {
          throw new Error(payload?.message || `Failed to load licenses (${res.status})`)
        }
        const rows = Array.isArray(payload.data) ? payload.data : []
        if (!cancelled) {
          setMunicipalityLicenses(
            rows.map((row: Record<string, unknown>) => mapMunicipalityLicenseRow(row)),
          )
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setMunicipalitiesError(e instanceof Error ? e.message : 'Failed to load licenses')
        }
      } finally {
        if (!cancelled) setMunicipalitiesLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [active])

  useEffect(() => {
    if (omniBootstrapComplete) return
    const stillLoading =
      jobsLoading || peopleLoading || assetsLoading || municipalitiesLoading
    if (!stillLoading) setOmniBootstrapComplete(true)
  }, [
    omniBootstrapComplete,
    jobsLoading,
    peopleLoading,
    assetsLoading,
    municipalitiesLoading,
  ])

  const fetchLinxupLocations = useCallback(async () => {
    const token = localStorage.getItem('lts_jwt_token')
    const headers: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
    const res = await apiFetch(`${API_BASE_URL}/integrations/linxup/locations`, { headers })
    const payload = await res.json().catch(() => ({}))
    if (!res.ok || !payload?.success) {
      throw new Error(payload?.message || `Failed to load Linxup locations (${res.status})`)
    }
    const entities = normalizeFleetEntities(payload.data)
    return entities.map(fleetEntityToControlCenterAsset)
  }, [])

  const fetchGroupLabels = useCallback(async () => {
    const token = localStorage.getItem('lts_jwt_token')
    const headers: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
    const res = await apiFetch(`${API_BASE_URL}/integrations/linxup/group-labels`, {
      headers,
      cache: 'no-store',
    })
    if (res.status === 304) return null
    const payload = await res.json().catch(() => ({}))
    if (!res.ok || !payload?.success) {
      throw new Error(payload?.message || 'Failed to load group labels')
    }
    const groups: TallyGroupLabelApiRow[] = Array.isArray(payload.groups) ? payload.groups : []
    const hiddenGroupIds: string[] = Array.isArray(payload.hiddenGroupIds)
      ? payload.hiddenGroupIds.map((x: unknown) => String(x))
      : []
    return { groups, hiddenGroupIds }
  }, [])

  const fetchAssetImageRows = useCallback(async () => {
    const token = localStorage.getItem('lts_jwt_token')
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
    const res = await apiFetch(`${API_BASE_URL}/capabilities/execute`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        app: 'tally',
        capability: 'assets.list',
        input: { includeLinxup: true },
      }),
    })
    const payload = await res.json().catch(() => ({}))
    if (!res.ok || !payload?.success) {
      throw new Error(payload?.message || 'Failed to load asset gallery data')
    }
    return Array.isArray(payload.data) ? payload.data : []
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setAssetsError(null)
        setAssetsLoading(true)
        const [next, labelPayload] = await Promise.all([
          fetchLinxupLocations(),
          fetchGroupLabels().catch(() => ({
            groups: [] as TallyGroupLabelApiRow[],
            hiddenGroupIds: [] as string[],
          })),
        ])
        if (cancelled) return
        setLinxupAssets(next)
        if (labelPayload) {
          setFleetGroupLabels(buildFleetIdLabelMap(labelPayload.groups))
          setHiddenFleetIds(buildHiddenFleetSet(labelPayload.hiddenGroupIds))
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setAssetsError(e instanceof Error ? e.message : 'Failed to load Linxup locations')
        }
      } finally {
        if (!cancelled) setAssetsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [fetchLinxupLocations, fetchGroupLabels])

  useEffect(() => {
    const id = window.setInterval(() => {
      fetchLinxupLocations()
        .then((next) => setLinxupAssets(next))
        .catch(() => {
          /* keep last good data */
        })
      fetchGroupLabels()
        .then((labelPayload) => {
          if (!labelPayload) return
          setFleetGroupLabels(buildFleetIdLabelMap(labelPayload.groups))
          setHiddenFleetIds(buildHiddenFleetSet(labelPayload.hiddenGroupIds))
        })
        .catch(() => {
          /* keep last good labels */
        })
    }, 45_000)
    return () => window.clearInterval(id)
  }, [fetchLinxupLocations, fetchGroupLabels])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setAssetImagesLoading(true)
        const rows = await fetchAssetImageRows()
        if (!cancelled) setAssetImageRows(rows)
      } catch {
        if (!cancelled) setAssetImageRows([])
      } finally {
        if (!cancelled) setAssetImagesLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [fetchAssetImageRows])

  const clearActiveStepResult = () => {
    setFocusedResultPointId(null)
    setOperationSteps((prev) =>
      prev.map((step) => (step.id === activeStepId ? { ...step, result: null } : step)),
    )
  }

  const nudgeCollapsedOutputBadge = useCallback(() => {
    if (outputOpenRef.current) return
    setOutputBadgeFlashToken((n) => n + 1)
  }, [])

  const selectEntityFromMap = useCallback((id: string, category: EntityCategory) => {
    const omniCategory = category as OmniCategory
    setOmniSelections((prev) => {
      if (prev.some((s) => s.id === id && s.category === omniCategory)) return prev
      return [...prev, { id, category: omniCategory }]
    })
    setFocusedResultPointId(null)
    setFocusId(id)
    setEntityFocusToken((n) => n + 1)
    if (category === 'municipality') {
      const ext = externalMunicipalitiesById[id]
      if (ext) setExternalMunicipality(ext)
    }
    if (isMobile) setOutputOpen(true)
    else nudgeCollapsedOutputBadge()
  }, [externalMunicipalitiesById, isMobile, nudgeCollapsedOutputBadge])

  const selectTableEntity = useCallback((category: 'job' | 'person', id: string) => {
    selectEntityFromMap(id, category)
    setOutputOpen(true)
  }, [selectEntityFromMap])

  useEffect(() => {
    if (typeof localStorage === 'undefined' || !isMapStyleId(mapStyleId)) return
    localStorage.setItem(MAP_STYLE_STORAGE_KEY, mapStyleId)
  }, [mapStyleId])

  useEffect(() => {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(OUTPUT_PANEL_STORAGE_KEY, outputOpen ? '1' : '0')
  }, [outputOpen])

  const filteredJobs = useMemo(() => {
    return jobtreadJobs
      .filter((job) => {
        if (jobFilters.activity.length > 0) {
          const tag = job.active ? 'active' : 'inactive'
          if (!jobFilters.activity.includes(tag)) return false
        }
        if (jobFilters.states.length > 0 && !jobFilters.states.includes(job.state)) return false
        if (
          jobFilters.projectTypes.length > 0 &&
          !jobFilters.projectTypes.includes(job.projectType)
        ) {
          return false
        }
        if (jobFilters.statuses.length > 0 && !jobFilters.statuses.includes(job.status)) {
          return false
        }
        return true
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [jobtreadJobs, jobFilters])

  const filteredPeople = useMemo(() => {
    return peopleWithJobLocations
      .filter((person) => {
        if (personFilters.kinds.length > 0 && !personFilters.kinds.includes(person.personKind)) {
          return false
        }
        if (personFilters.clockedIn.length > 0) {
          const tag = person.clockedIn ? 'yes' : 'no'
          if (!personFilters.clockedIn.includes(tag)) return false
        }
        return true
      })
      .sort((a, b) => {
        if (a.clockedIn !== b.clockedIn) return a.clockedIn ? -1 : 1
        return a.name.localeCompare(b.name)
      })
  }, [peopleWithJobLocations, personFilters])

  const filteredAssets = useMemo(() => {
    return linxupAssets
      .filter((asset) => !hiddenFleetIds.has(asset.fleetId))
      .filter((asset) => {
        if (assetFilters.kinds.length > 0 && !assetFilters.kinds.includes(asset.assetKind)) {
          return false
        }
        if (assetFilters.statuses.length > 0 && !assetFilters.statuses.includes(asset.status)) {
          return false
        }
        return true
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }))
  }, [linxupAssets, hiddenFleetIds, assetFilters])

  const filteredMunicipalities = useMemo(() => {
    return municipalityLicenses
      .filter((muni) => {
        if (municipalityFilters.states.length > 0 && !municipalityFilters.states.includes(muni.state)) {
          return false
        }
        if (municipalityFilters.levels.length > 0 && !municipalityFilters.levels.includes(muni.level)) {
          return false
        }
        if (
          municipalityFilters.licenseTypes.length > 0 &&
          !municipalityFilters.licenseTypes.includes(muni.licenseType)
        ) {
          return false
        }
        if (municipalityFilters.statuses.length > 0) {
          const expired = municipalityLicenseIsExpired(muni)
          const expiring = municipalityLicenseIsExpiringSoon(muni)
          const matches =
            (municipalityFilters.statuses.includes('expired') && expired) ||
            (municipalityFilters.statuses.includes('current') && !expired) ||
            (municipalityFilters.statuses.includes('expiring') && expiring)
          if (!matches) return false
        }
        return true
      })
      .sort((a, b) => {
        const s = a.state.localeCompare(b.state)
        if (s !== 0) return s
        const t = a.licenseType.localeCompare(b.licenseType)
        if (t !== 0) return t
        return a.name.localeCompare(b.name)
      })
  }, [municipalityLicenses, municipalityFilters])

  const omniJobStates = useMemo(
    () => [...new Set(jobtreadJobs.map((j) => j.state).filter(Boolean))].sort(),
    [jobtreadJobs],
  )
  const omniJobProjectTypeOptions = useMemo(
    () =>
      [...new Set(jobtreadJobs.map((j) => j.projectType).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [jobtreadJobs],
  )
  const omniJobStatusOptions = useMemo(
    () =>
      [...new Set(jobtreadJobs.map((j) => j.status).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [jobtreadJobs],
  )
  const omniAssetStatusOptions = useMemo(
    () =>
      [...new Set(linxupAssets.map((a) => a.status).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [linxupAssets],
  )
  const omniMunicipalityStates = useMemo(
    () => [...new Set(municipalityLicenses.map((m) => m.state))].sort(),
    [municipalityLicenses],
  )

  const toggleOmniFilterType = useCallback((category: OmniCategory) => {
    setOmniActiveFilterTypes([category])
  }, [])

  const handleFiltersOpenChange = useCallback((open: boolean) => {
    setOmniFiltersOpen(open)
    if (open) {
      setOmniActiveFilterTypes((prev) => (prev.length > 0 ? prev : ['job']))
    }
  }, [])

  useEffect(() => {
    if (!weatherEnabled) {
      setWeatherFrames([])
      return
    }

    let cancelled = false
    const controller = new AbortController()

    const load = async () => {
      try {
        const frames = await fetchRainViewerRadarFrames(controller.signal)
        if (cancelled) return
        setWeatherFrames(frames)
      } catch (e) {
        if (cancelled || (e instanceof DOMException && e.name === 'AbortError')) return
        setWeatherFrames([])
      }
    }

    void load()
    const refreshId = window.setInterval(() => {
      void load()
    }, 5 * 60_000)

    return () => {
      cancelled = true
      controller.abort()
      window.clearInterval(refreshId)
    }
  }, [weatherEnabled])

  // Current radar only — scrub/play UI hidden until rate-limit friendly playback is ready.
  const weatherFrame =
    weatherFrames.length > 0 ? weatherFrames[weatherFrames.length - 1]! : null

  useEffect(() => {
    const activeLevels = BOUNDARY_OVERLAY_LEVELS.filter((level) => boundaryOverlays[level])
    if (activeLevels.length === 0) {
      setBoundaryOverlayGeojson({ state: null, county: null, city: null })
      setBoundaryOverlaysLoading(false)
      return
    }

    const controller = new AbortController()
    let cancelled = false

    const geoidsForOverlay = (level: TigerwebBoundaryLevel): string[] => {
      if (level === 'state') {
        // States implied by current filter results (e.g. contractor licenses in UT → UT outline)
        const states = [
          ...new Set(filteredMunicipalities.map((m) => m.state).filter(Boolean)),
        ]
        return [
          ...new Set(
            municipalityLicenses
              .filter(
                (m) =>
                  m.level === 'state' &&
                  states.includes(m.state) &&
                  Boolean(m.censusGeoid),
              )
              .map((m) => String(m.censusGeoid)),
          ),
        ]
      }
      return [
        ...new Set(
          filteredMunicipalities
            .filter((m) => m.level === level && Boolean(m.censusGeoid))
            .map((m) => String(m.censusGeoid)),
        ),
      ]
    }

    ;(async () => {
      setBoundaryOverlaysLoading(true)
      const next: Record<TigerwebBoundaryLevel, TigerwebFeatureCollection | null> = {
        state: null,
        county: null,
        city: null,
      }

      try {
        await Promise.all(
          BOUNDARY_OVERLAY_LEVELS.map(async (level) => {
            if (!boundaryOverlays[level]) {
              next[level] = null
              return
            }
            const geoids = geoidsForOverlay(level)
            if (geoids.length === 0) {
              next[level] = { type: 'FeatureCollection', features: [] }
              return
            }
            next[level] = await fetchTigerwebBoundaries(level, geoids, {
              signal: controller.signal,
              concurrency: 4,
            })
          }),
        )
        if (!cancelled) setBoundaryOverlayGeojson(next)
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (!cancelled) {
          setBoundaryOverlayGeojson({ state: null, county: null, city: null })
        }
      } finally {
        if (!cancelled) setBoundaryOverlaysLoading(false)
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [boundaryOverlays, filteredMunicipalities, municipalityLicenses])

  const mapEntities: MappableEntity[] = useMemo(() => {
    const entities: MappableEntity[] = []

    const jobById = new Map(jobtreadJobs.map((j) => [j.id, j]))
    const personById = new Map(peopleWithJobLocations.map((p) => [p.id, p]))
    const assetById = new Map(linxupAssets.map((a) => [a.id, a]))
    const muniById = new Map(municipalityLicenses.map((m) => [m.id, m]))
    const jobCoordKeys = new Set(
      filteredJobs.map((j) => `${j.lat.toFixed(5)},${j.lng.toFixed(5)}`),
    )
    const seen = new Set<string>()

    const pushJob = (job: ControlCenterJob) => {
      const key = `job:${job.id}`
      if (seen.has(key) || !Number.isFinite(job.lat) || !Number.isFinite(job.lng)) return
      seen.add(key)
      entities.push({
        id: job.id,
        category: 'job',
        lat: job.lat,
        lng: job.lng,
        color: jobStatusColor(job.status),
        icon: faBuilding,
        title: job.name,
        subtitle: [
          job.number ? `#${job.number}` : null,
          titleCase(job.status || 'unknown'),
          job.active ? 'Active' : 'Inactive',
        ]
          .filter(Boolean)
          .join(' · '),
        detail:
          [
            job.address || job.locationName || null,
            [job.city, job.state].filter(Boolean).join(', ') || null,
          ]
            .filter(Boolean)
            .join(' · ') || undefined,
      })
    }

    const pushPerson = (person: ControlCenterPerson) => {
      const key = `person:${person.id}`
      if (seen.has(key) || !personHasMapPin(person)) return
      const geo = normalizeLatLng(person.lat, person.lng)
      if (!geo) return
      seen.add(key)
      const besideJob = jobCoordKeys.has(`${geo.lat.toFixed(5)},${geo.lng.toFixed(5)}`)
      const [markerLat, markerLng] = personMarkerPosition(geo.lat, geo.lng, besideJob)
      entities.push({
        id: person.id,
        category: 'person',
        lat: markerLat,
        lng: markerLng,
        color: PERSON_KIND_COLOR[person.personKind],
        icon: faUser,
        title: person.name,
        subtitle: [
          personKindLabel(person.personKind),
          person.clockedIn ? 'Clocked in' : 'Off clock',
        ].join(' · '),
        detail:
          [person.roleName || null, person.jobName || null].filter(Boolean).join(' · ') ||
          undefined,
      })
    }

    const pushAsset = (asset: ControlCenterAsset) => {
      const key = `asset:${asset.id}`
      if (seen.has(key) || !Number.isFinite(asset.lat) || !Number.isFinite(asset.lng)) return
      seen.add(key)
      entities.push({
        id: asset.id,
        category: 'asset',
        lat: asset.lat,
        lng: asset.lng,
        color: ASSET_KIND_COLOR[asset.assetKind],
        icon: asset.assetKind === 'assetTracker' ? faSatelliteDish : faTruck,
        title: asset.name,
        subtitle: `${assetKindLabel(asset.assetKind)} · ${asset.status}`,
        detail: asset.driver ? `Driver · ${asset.driver}` : undefined,
      })
    }

    for (const sel of omniSelections) {
      if (sel.category === 'job') {
        const job = jobById.get(sel.id)
        if (job) pushJob(job)
        continue
      }
      if (sel.category === 'person') {
        const person = personById.get(sel.id)
        if (person) pushPerson(person)
        continue
      }
      if (sel.category === 'asset') {
        const asset = assetById.get(sel.id)
        if (asset) pushAsset(asset)
        continue
      }
      if (sel.category === 'municipality') {
        const muni =
          muniById.get(sel.id) ??
          externalMunicipalitiesById[sel.id] ??
          (externalMunicipality?.id === sel.id ? externalMunicipality : null)
        if (!muni || !Number.isFinite(muni.lat) || !Number.isFinite(muni.lng)) continue
        const key = `municipality:${muni.id}`
        if (seen.has(key)) continue
        seen.add(key)
        entities.push({
          id: muni.id,
          category: 'municipality',
          lat: muni.lat,
          lng: muni.lng,
          color: muni.isExternal
            ? EXTERNAL_MUNICIPALITY_COLOR
            : MUNICIPALITY_LEVEL_COLOR[muni.level],
          icon: faLandmark,
          title: muni.name,
          subtitle: muni.isExternal
            ? `Census ${titleCase(muni.level)} · Not in DB`
            : `${licenseTypeLabel(muni.licenseType)} · ${titleCase(muni.level)}`,
          detail: muni.isExternal
            ? [muni.state, muni.censusGeoid].filter(Boolean).join(' · ') || undefined
            : [muni.state, muni.licenseNumber].filter(Boolean).join(' · ') || undefined,
        })
      }
    }

    if (mapMarkerVisibility.jobs) {
      for (const job of filteredJobs) pushJob(job)
    }
    if (mapMarkerVisibility.people) {
      for (const person of filteredPeople) pushPerson(person)
    }
    if (mapMarkerVisibility.assets) {
      for (const asset of filteredAssets) pushAsset(asset)
    }

    return entities
  }, [
    omniSelections,
    jobtreadJobs,
    peopleWithJobLocations,
    linxupAssets,
    municipalityLicenses,
    externalMunicipalitiesById,
    externalMunicipality,
    mapMarkerVisibility,
    filteredJobs,
    filteredPeople,
    filteredAssets,
  ])

  const selectedIds = useMemo(() => {
    return new Set(omniSelections.map((s) => s.id))
  }, [omniSelections])

  const omniHits = useMemo((): OmniSearchHit[] => {
    const q = omniQuery.trim().toLowerCase()
    if (q.length < 1) return []
    const selected = new Set(omniSelections.map((s) => `${s.category}:${s.id}`))
    const hits: OmniSearchHit[] = []
    const seen = new Set<string>()
    const pushCapped = (category: OmniCategory, items: OmniSearchHit[]) => {
      let n = 0
      for (const item of items) {
        if (n >= OMNI_RESULT_CAP_PER_GROUP) break
        const key = `${item.category}:${item.id}`
        if (seen.has(key) || selected.has(key)) continue
        seen.add(key)
        hits.push(item)
        n += 1
      }
    }

    type RankedHit = OmniSearchHit & { related: boolean }
    const rank = (items: RankedHit[]) =>
      items.sort((a, b) => {
        if (a.related !== b.related) return a.related ? 1 : -1
        return a.name.localeCompare(b.name)
      })

    const directJobs: RankedHit[] = filteredJobs
      .filter((job) => omniQueryMatches(q, jobOmniHaystack(job)))
      .map((job) => ({
        id: job.id,
        category: 'job' as const,
        name: job.name,
        detail: [
          job.number ? `#${job.number}` : null,
          titleCase(job.status || 'unknown'),
          job.accountName || null,
        ]
          .filter(Boolean)
          .join(' · '),
        color: jobStatusColor(job.status),
        group: OMNI_GROUP_LABEL.job,
        related: false,
      }))

    const directPeople: RankedHit[] = filteredPeople
      .filter((person) => omniQueryMatches(q, personOmniHaystack(person)))
      .map((person) => ({
        id: person.id,
        category: 'person' as const,
        name: person.name,
        detail: [
          personKindLabel(person.personKind),
          person.roleName || null,
          person.jobName || null,
        ]
          .filter(Boolean)
          .join(' · '),
        color: PERSON_KIND_COLOR[person.personKind],
        group: OMNI_GROUP_LABEL.person,
        related: false,
      }))

    const directAssets: RankedHit[] = filteredAssets
      .filter((asset) => omniQueryMatches(q, assetOmniHaystack(asset)))
      .map((asset) => {
        const driver = assetDriverName(asset)
        return {
          id: asset.id,
          category: 'asset' as const,
          name: asset.name,
          detail: [
            assetKindLabel(asset.assetKind),
            asset.status || null,
            driver ? `Driver · ${driver}` : null,
          ]
            .filter(Boolean)
            .join(' · '),
          color: ASSET_KIND_COLOR[asset.assetKind],
          group: OMNI_GROUP_LABEL.asset,
          related: false,
        }
      })

    const relatedPeople: RankedHit[] = []
    const relatedAssets: RankedHit[] = []
    const relatedJobs: RankedHit[] = []
    const directPersonIds = new Set(directPeople.map((hit) => hit.id))
    const directAssetIds = new Set(directAssets.map((hit) => hit.id))
    const directJobIds = new Set(directJobs.map((hit) => hit.id))
    const jobsById = new Map(filteredJobs.map((job) => [job.id, job]))

    // Employee → assigned vehicle(s) via Linxup driver name.
    for (const person of filteredPeople) {
      if (!directPersonIds.has(person.id)) continue
      for (const asset of filteredAssets) {
        if (directAssetIds.has(asset.id)) continue
        const driver = assetDriverName(asset)
        if (!omniNamesLooselyMatch(driver, person.name)) continue
        relatedAssets.push({
          id: asset.id,
          category: 'asset',
          name: asset.name,
          detail: [
            assetKindLabel(asset.assetKind),
            `Assigned to ${person.name}`,
          ].join(' · '),
          color: ASSET_KIND_COLOR[asset.assetKind],
          group: OMNI_GROUP_LABEL.asset,
          related: true,
        })
      }
      // Clocked-in / assigned job for a matched person.
      if (person.jobId && !directJobIds.has(person.jobId)) {
        const job = jobsById.get(person.jobId)
        if (job) {
          relatedJobs.push({
            id: job.id,
            category: 'job',
            name: job.name,
            detail: [
              job.number ? `#${job.number}` : null,
              `With ${person.name}`,
            ]
              .filter(Boolean)
              .join(' · '),
            color: jobStatusColor(job.status),
            group: OMNI_GROUP_LABEL.job,
            related: true,
          })
        }
      }
    }

    // Vehicle/driver match → related employee.
    for (const asset of filteredAssets) {
      if (!directAssetIds.has(asset.id)) continue
      const driver = assetDriverName(asset)
      if (!driver) continue
      for (const person of filteredPeople) {
        if (directPersonIds.has(person.id)) continue
        if (!omniNamesLooselyMatch(driver, person.name)) continue
        relatedPeople.push({
          id: person.id,
          category: 'person',
          name: person.name,
          detail: [
            personKindLabel(person.personKind),
            `Drives ${asset.name}`,
          ].join(' · '),
          color: PERSON_KIND_COLOR[person.personKind],
          group: OMNI_GROUP_LABEL.person,
          related: true,
        })
      }
    }

    // Job match → people currently tied to that job.
    for (const job of filteredJobs) {
      if (!directJobIds.has(job.id)) continue
      for (const person of filteredPeople) {
        if (directPersonIds.has(person.id)) continue
        if (person.jobId !== job.id) continue
        relatedPeople.push({
          id: person.id,
          category: 'person',
          name: person.name,
          detail: [
            personKindLabel(person.personKind),
            `On ${job.name}`,
          ].join(' · '),
          color: PERSON_KIND_COLOR[person.personKind],
          group: OMNI_GROUP_LABEL.person,
          related: true,
        })
      }
    }

    pushCapped('job', rank([...directJobs, ...relatedJobs]))
    pushCapped('person', rank([...directPeople, ...relatedPeople]))
    pushCapped('asset', rank([...directAssets, ...relatedAssets]))

    const dbMunicipalityHits: OmniSearchHit[] = filteredMunicipalities
      .filter((muni) => omniQueryMatches(q, municipalityOmniHaystack(muni)))
      .map((muni) => ({
        id: muni.id,
        category: 'municipality' as const,
        name: muni.name,
        detail: [
          muni.state || null,
          licenseTypeLabel(muni.licenseType),
          muni.licenseNumber ? `#${muni.licenseNumber}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        color: MUNICIPALITY_LEVEL_COLOR[muni.level],
        group: OMNI_GROUP_LABEL.municipality,
      }))

    const censusMunicipalityHits: OmniSearchHit[] = omniCensusHits
      .filter((hit) => !selected.has(`municipality:${censusMunicipalityId(hit.level, hit.geoid)}`))
      .map((hit) => {
        const abbr = stateAbbrFromCensusHit(hit)
        return {
          id: censusMunicipalityId(hit.level, hit.geoid),
          category: 'municipality' as const,
          name: hit.basename || hit.name,
          detail: [abbr, titleCase(hit.level), 'Unlicensed · not in DB']
            .filter(Boolean)
            .join(' · '),
          color: EXTERNAL_MUNICIPALITY_COLOR,
          group: OMNI_GROUP_LABEL.municipality,
          isExternal: true,
          censusHit: hit,
        }
      })

    // Keep licensed matches first, then Census places not in the DB.
    for (const item of [...dbMunicipalityHits.slice(0, 6), ...censusMunicipalityHits.slice(0, 8)]) {
      const key = `${item.category}:${item.id}`
      if (seen.has(key) || selected.has(key)) continue
      seen.add(key)
      hits.push(item)
    }
    return hits
  }, [
    omniQuery,
    omniSelections,
    filteredJobs,
    filteredPeople,
    filteredAssets,
    filteredMunicipalities,
    omniCensusHits,
  ])

  const knownCensusGeoids = useMemo(() => {
    const set = new Set<string>()
    for (const m of municipalityLicenses) {
      if (m.censusGeoid) set.add(m.censusGeoid)
    }
    return set
  }, [municipalityLicenses])

  const omniCensusSearchLevels = useMemo((): TigerwebBoundaryLevel[] => {
    const fromFilters = municipalityFilters.levels.filter(isTigerwebBoundaryLevel)
    if (fromFilters.length > 0) return fromFilters
    return ['city', 'county', 'state']
  }, [municipalityFilters.levels])

  const omniCensusStateFips = useMemo(() => {
    if (municipalityFilters.states.length !== 1) return null
    return STATE_ABBR_TO_FIPS[municipalityFilters.states[0]] || null
  }, [municipalityFilters.states])

  useEffect(() => {
    const q = omniQuery.trim()
    if (q.length < 2) {
      setOmniCensusHits([])
      setOmniCensusSearching(false)
      return
    }

    const controller = new AbortController()
    const timer = window.setTimeout(async () => {
      setOmniCensusSearching(true)
      try {
        const settled = await Promise.allSettled(
          omniCensusSearchLevels.map((level) =>
            searchTigerwebJurisdictions(level, q, {
              signal: controller.signal,
              stateFips: omniCensusStateFips,
              limit: 8,
            }),
          ),
        )
        if (controller.signal.aborted) return
        const merged: TigerwebSearchHit[] = []
        const seen = new Set<string>()
        for (const result of settled) {
          if (result.status !== 'fulfilled') continue
          for (const hit of result.value) {
            const key = `${hit.level}:${hit.geoid}`
            if (seen.has(key)) continue
            if (knownCensusGeoids.has(hit.geoid)) continue
            seen.add(key)
            merged.push(hit)
          }
        }
        merged.sort((a, b) => {
          const byName = a.name.localeCompare(b.name)
          if (byName !== 0) return byName
          return a.level.localeCompare(b.level)
        })
        setOmniCensusHits(merged.slice(0, 16))
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') return
        if (!controller.signal.aborted) setOmniCensusHits([])
      } finally {
        if (!controller.signal.aborted) setOmniCensusSearching(false)
      }
    }, 300)

    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [omniQuery, omniCensusSearchLevels, omniCensusStateFips, knownCensusGeoids])

  const omniBadges = useMemo(() => {
    const jobById = new Map(jobtreadJobs.map((j) => [j.id, j]))
    const personById = new Map(peopleWithJobLocations.map((p) => [p.id, p]))
    const assetById = new Map(linxupAssets.map((a) => [a.id, a]))
    const muniById = new Map(municipalityLicenses.map((m) => [m.id, m]))
    const badges: Array<{ id: string; category: OmniCategory; name: string; color: string }> = []
    for (const sel of omniSelections) {
      if (sel.category === 'job') {
        const job = jobById.get(sel.id)
        if (!job) continue
        badges.push({
          id: job.id,
          category: 'job',
          name: job.name,
          color: jobStatusColor(job.status),
        })
      } else if (sel.category === 'person') {
        const person = personById.get(sel.id)
        if (!person) continue
        badges.push({
          id: person.id,
          category: 'person',
          name: person.name,
          color: PERSON_KIND_COLOR[person.personKind],
        })
      } else if (sel.category === 'asset') {
        const asset = assetById.get(sel.id)
        if (!asset) continue
        badges.push({
          id: asset.id,
          category: 'asset',
          name: asset.name,
          color: ASSET_KIND_COLOR[asset.assetKind],
        })
      } else {
        const muni =
          muniById.get(sel.id) ??
          externalMunicipalitiesById[sel.id] ??
          (externalMunicipality?.id === sel.id ? externalMunicipality : null)
        if (!muni) continue
        badges.push({
          id: muni.id,
          category: 'municipality',
          name: muni.isExternal ? `${muni.name} (not in DB)` : muni.name,
          color: muni.isExternal
            ? EXTERNAL_MUNICIPALITY_COLOR
            : MUNICIPALITY_LEVEL_COLOR[muni.level],
        })
      }
    }
    return badges
  }, [
    omniSelections,
    jobtreadJobs,
    peopleWithJobLocations,
    linxupAssets,
    municipalityLicenses,
    externalMunicipalitiesById,
    externalMunicipality,
  ])

  const collapsedOutputBadgeCount = useMemo(() => {
    const selectedCount = omniSelections.length
    const listedOperations = operationSteps.filter(
      (step) => Boolean(step.result) || Boolean(step.operatorId),
    ).length
    return selectedCount + listedOperations
  }, [omniSelections.length, operationSteps])

  const selectOmniHit = useCallback(
    async (hit: OmniSearchHit) => {
      if (hit.censusHit) {
        const censusHit = hit.censusHit
        const id = censusMunicipalityId(censusHit.level, censusHit.geoid)
        if (externalMunicipalitiesById[id] || externalMunicipality?.id === id) {
          setOmniSelections((prev) => {
            if (prev.some((s) => s.id === id && s.category === 'municipality')) return prev
            return [...prev, { id, category: 'municipality' }]
          })
          setFocusedResultPointId(null)
          setFocusId(id)
          setEntityFocusToken((n) => n + 1)
          setExternalMunicipality(
            externalMunicipalitiesById[id] ?? externalMunicipality,
          )
          setOmniQuery('')
          setOmniOpen(false)
          if (isMobile) setOutputOpen(true)
          else nudgeCollapsedOutputBadge()
          return
        }

        setCensusPlaceResolvingId(id)
        try {
          const fc = await fetchTigerwebBoundary(censusHit.level, censusHit.geoid)
          const center = featureCentroid(fc.features[0])
          if (!center) {
            throw new Error('Could not locate this Census place on the map')
          }
          const muni = municipalityFromCensusHit(censusHit, {
            lat: Number(center.lat.toFixed(6)),
            lng: Number(center.lng.toFixed(6)),
          })
          boundaryRequestIdRef.current += 1
          setExternalMunicipality(muni)
          setExternalMunicipalitiesById((prev) => ({ ...prev, [muni.id]: muni }))
          setOmniSelections((prev) => {
            if (prev.some((s) => s.id === muni.id && s.category === 'municipality')) return prev
            return [...prev, { id: muni.id, category: 'municipality' }]
          })
          setFocusedResultPointId(null)
          setFocusId(muni.id)
          setEntityFocusToken((n) => n + 1)
          setBoundaryGeojson(fc)
          setBoundaryForId(muni.id)
          setBoundaryLoading(false)
          setOmniQuery('')
          setOmniOpen(false)
          if (isMobile) setOutputOpen(true)
          else nudgeCollapsedOutputBadge()
        } catch (e: unknown) {
          setMunicipalitiesError(
            e instanceof Error ? e.message : 'Failed to load Census place on the map',
          )
        } finally {
          setCensusPlaceResolvingId(null)
        }
        return
      }

      setOmniSelections((prev) => {
        if (prev.some((s) => s.id === hit.id && s.category === hit.category)) return prev
        return [...prev, { id: hit.id, category: hit.category }]
      })
      setFocusedResultPointId(null)
      setFocusId(hit.id)
      setEntityFocusToken((n) => n + 1)
      setOmniQuery('')
      setOmniOpen(false)
      if (isMobile) setOutputOpen(true)
      else nudgeCollapsedOutputBadge()
    },
    [externalMunicipalitiesById, externalMunicipality, isMobile, nudgeCollapsedOutputBadge],
  )

  const removeOmniSelection = useCallback((id: string) => {
    setFocusedResultPointId(null)
    setOperationSteps((prev) => {
      const cleared = clearOperationResultsForParentEntities(prev, new Set([id]))
      // After the last pin is removed, drop orphaned selection-backed ops so the
      // collapsed chip doesn't keep showing a stale count.
      const stillPinned = omniSelections.some((s) => s.id !== id)
      return stillPinned ? cleared : clearSelectionBackedOperationSteps(cleared)
    })
    setOmniSelections((prev) => {
      const next = prev.filter((s) => s.id !== id)
      setFocusId((current) => {
        if (current !== id) return current
        return next.length > 0 ? next[next.length - 1].id : null
      })
      const removedWasMuni = prev.some((s) => s.id === id && s.category === 'municipality')
      if (removedWasMuni) {
        boundaryRequestIdRef.current += 1
        setBoundaryGeojson(null)
        setBoundaryForId(null)
        setBoundaryLoading(false)
      }
      return next
    })
    setExternalMunicipalitiesById((prev) => {
      if (!prev[id]) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
    setExternalMunicipality((prev) => (prev?.id === id ? null : prev))
  }, [omniSelections])

  const clearOmniSelections = useCallback(() => {
    if (omniSelections.length === 0) return
    const removedIds = new Set(omniSelections.map((s) => s.id))
    const hadMuni = omniSelections.some((s) => s.category === 'municipality')
    setFocusedResultPointId(null)
    setFocusId(null)
    setOperationSteps((steps) =>
      clearSelectionBackedOperationSteps(
        clearOperationResultsForParentEntities(steps, removedIds),
      ),
    )
    setOmniSelections([])
    if (hadMuni) {
      boundaryRequestIdRef.current += 1
      setBoundaryGeojson(null)
      setBoundaryForId(null)
      setBoundaryLoading(false)
    }
    setExternalMunicipalitiesById({})
    setExternalMunicipality(null)
  }, [omniSelections])

  const focusOmniSelection = useCallback(
    (id: string, category: OmniCategory) => {
      setFocusedResultPointId(null)
      setFocusId(id)
      setEntityFocusToken((n) => n + 1)
      setOutputOpen(true)
      if (category === 'municipality') {
        const ext = externalMunicipalitiesById[id]
        if (ext) setExternalMunicipality(ext)
      }
    },
    [externalMunicipalitiesById],
  )

  const selectedJob = useMemo(() => {
    const sel = omniSelections.find((s) => s.category === 'job' && s.id === focusId)
    if (!sel) return null
    return jobtreadJobs.find((j) => j.id === sel.id) ?? null
  }, [jobtreadJobs, omniSelections, focusId])

  const selectedPerson = useMemo(() => {
    const sel = omniSelections.find((s) => s.category === 'person' && s.id === focusId)
    if (!sel) return null
    return peopleWithJobLocations.find((p) => p.id === sel.id) ?? null
  }, [peopleWithJobLocations, omniSelections, focusId])

  const selectedAsset = useMemo(() => {
    const sel = omniSelections.find((s) => s.category === 'asset' && s.id === focusId)
    if (!sel) return null
    return linxupAssets.find((a) => a.id === sel.id) ?? null
  }, [linxupAssets, omniSelections, focusId])

  const pinnedJobs = useMemo(() => {
    const byId = new Map(jobtreadJobs.map((j) => [j.id, j]))
    return omniSelections
      .filter((s) => s.category === 'job')
      .map((s) => byId.get(s.id))
      .filter((j): j is ControlCenterJob => Boolean(j))
  }, [omniSelections, jobtreadJobs])

  const pinnedPeople = useMemo(() => {
    const byId = new Map(peopleWithJobLocations.map((p) => [p.id, p]))
    return omniSelections
      .filter((s) => s.category === 'person')
      .map((s) => byId.get(s.id))
      .filter((p): p is ControlCenterPerson => Boolean(p))
  }, [omniSelections, peopleWithJobLocations])

  const pinnedAssets = useMemo(() => {
    const byId = new Map(linxupAssets.map((a) => [a.id, a]))
    return omniSelections
      .filter((s) => s.category === 'asset')
      .map((s) => byId.get(s.id))
      .filter((a): a is ControlCenterAsset => Boolean(a))
  }, [omniSelections, linxupAssets])

  const pinnedMunicipalities = useMemo(() => {
    return omniSelections
      .filter((s) => s.category === 'municipality')
      .map((s) => {
        return (
          municipalityLicenses.find((m) => m.id === s.id) ??
          externalMunicipalitiesById[s.id] ??
          (externalMunicipality?.id === s.id ? externalMunicipality : null)
        )
      })
      .filter((m): m is ControlCenterMunicipality => Boolean(m))
  }, [
    omniSelections,
    municipalityLicenses,
    externalMunicipalitiesById,
    externalMunicipality,
  ])

  const pinnedAssetIssuesById = useMemo(() => {
    const map = new Map<string, FleetIssue[]>()
    for (const asset of pinnedAssets) {
      map.set(
        asset.id,
        deriveFleetIssues({
          key: asset.id,
          lat: asset.lat,
          lng: asset.lng,
          displayName: asset.name,
          assetTracker: asset.assetKind === 'assetTracker',
          fleetId: asset.fleetId,
          raw: asset.raw,
        }),
      )
    }
    return map
  }, [pinnedAssets])

  const selectedAssetImages = useMemo(
    () => (selectedAsset ? matchAssetImageUrls(selectedAsset, assetImageRows) : []),
    [selectedAsset, assetImageRows],
  )

  useEffect(() => {
    setAssetGalleryIdx(0)
  }, [selectedAsset?.id])

  const selectedMunicipality = useMemo(() => {
    const muniSels = omniSelections.filter((s) => s.category === 'municipality')
    if (muniSels.length === 0) return null
    const focused = focusId ? muniSels.find((s) => s.id === focusId) : null
    const pick = focused ?? muniSels[muniSels.length - 1]
    return (
      municipalityLicenses.find((m) => m.id === pick.id) ??
      externalMunicipalitiesById[pick.id] ??
      (externalMunicipality?.id === pick.id ? externalMunicipality : null)
    )
  }, [
    municipalityLicenses,
    externalMunicipality,
    externalMunicipalitiesById,
    omniSelections,
    focusId,
  ])

  const focusedEntity = useMemo((): MappableEntity | null => {
    if (!focusId) return null

    // Prefer the selected domain entity so we can fly even when markers are
    // hidden, and when person lat/lng were filled in via job-location fallback.
    if (selectedPerson && selectedPerson.id === focusId && personHasMapPin(selectedPerson)) {
      const geo = normalizeLatLng(selectedPerson.lat, selectedPerson.lng)
      if (!geo) return mapEntities.find((e) => e.id === focusId) ?? null
      return {
        id: selectedPerson.id,
        category: 'person',
        lat: geo.lat,
        lng: geo.lng,
        color: PERSON_KIND_COLOR[selectedPerson.personKind],
        icon: faUser,
        title: selectedPerson.name,
        subtitle: [
          personKindLabel(selectedPerson.personKind),
          selectedPerson.clockedIn ? 'Clocked in' : 'Off clock',
        ].join(' · '),
        detail:
          [selectedPerson.roleName || null, selectedPerson.jobName || null]
            .filter(Boolean)
            .join(' · ') || undefined,
      }
    }
    if (selectedJob && selectedJob.id === focusId) {
      return {
        id: selectedJob.id,
        category: 'job',
        lat: selectedJob.lat,
        lng: selectedJob.lng,
        color: jobStatusColor(selectedJob.status),
        icon: faBuilding,
        title: selectedJob.name,
        subtitle: [
          selectedJob.number ? `#${selectedJob.number}` : null,
          titleCase(selectedJob.status || 'unknown'),
          selectedJob.active ? 'Active' : 'Inactive',
        ]
          .filter(Boolean)
          .join(' · '),
        detail:
          [
            selectedJob.address || selectedJob.locationName || null,
            [selectedJob.city, selectedJob.state].filter(Boolean).join(', ') || null,
          ]
            .filter(Boolean)
            .join(' · ') || undefined,
      }
    }
    if (selectedAsset && selectedAsset.id === focusId) {
      return {
        id: selectedAsset.id,
        category: 'asset',
        lat: selectedAsset.lat,
        lng: selectedAsset.lng,
        color: ASSET_KIND_COLOR[selectedAsset.assetKind],
        icon: selectedAsset.assetKind === 'assetTracker' ? faSatelliteDish : faTruck,
        title: selectedAsset.name,
        subtitle: `${assetKindLabel(selectedAsset.assetKind)} · ${selectedAsset.status}`,
        detail: selectedAsset.driver ? `Driver · ${selectedAsset.driver}` : undefined,
      }
    }
    if (selectedMunicipality && selectedMunicipality.id === focusId) {
      return {
        id: selectedMunicipality.id,
        category: 'municipality',
        lat: selectedMunicipality.lat,
        lng: selectedMunicipality.lng,
        color: selectedMunicipality.isExternal
          ? EXTERNAL_MUNICIPALITY_COLOR
          : MUNICIPALITY_LEVEL_COLOR[selectedMunicipality.level],
        icon: faLandmark,
        title: selectedMunicipality.name,
        subtitle: selectedMunicipality.isExternal
          ? `Census ${titleCase(selectedMunicipality.level)} · Not in DB`
          : `${licenseTypeLabel(selectedMunicipality.licenseType)} · ${titleCase(selectedMunicipality.level)}`,
        detail: selectedMunicipality.isExternal
          ? [selectedMunicipality.state, selectedMunicipality.censusGeoid]
              .filter(Boolean)
              .join(' · ') || undefined
          : [selectedMunicipality.state, selectedMunicipality.licenseNumber]
              .filter(Boolean)
              .join(' · ') || undefined,
      }
    }
    return mapEntities.find((e) => e.id === focusId) ?? null
  }, [
    focusId,
    selectedPerson,
    selectedJob,
    selectedAsset,
    selectedMunicipality,
    mapEntities,
  ])

  const activeOperationStep = useMemo(
    () => operationSteps.find((s) => s.id === activeStepId) ?? operationSteps[0] ?? null,
    [operationSteps, activeStepId],
  )

  const operationPrimary = useMemo(
    () =>
      activeOperationStep
        ? resolveStepPrimary(activeOperationStep, operationSteps, {
            job: selectedJob,
            person: selectedPerson,
            asset: selectedAsset,
            municipality: selectedMunicipality,
          })
        : null,
    [
      activeOperationStep,
      operationSteps,
      selectedJob,
      selectedPerson,
      selectedAsset,
      selectedMunicipality,
    ],
  )

  const operationResult = activeOperationStep?.result ?? null
  const operationInputs = activeOperationStep?.inputs ?? DEFAULT_OPERATION_INPUTS

  /** Active result pins, or prior-step pins while the active step is still empty. */
  const displayedResultMapPoints = useMemo(() => {
    if (operationResult && operationResult.mapPoints.length > 0) {
      return operationResult.mapPoints.map((point) => ({
        point,
        fromStepId: activeStepId,
        interactive: false,
      }))
    }
    const activeIdx = Math.max(
      0,
      operationSteps.findIndex((s) => s.id === activeStepId),
    )
    const points: Array<{
      point: OperationMapPoint
      fromStepId: string
      interactive: boolean
    }> = []
    for (let i = 0; i < activeIdx; i += 1) {
      const step = operationSteps[i]
      for (const point of step.result?.mapPoints ?? []) {
        points.push({ point, fromStepId: step.id, interactive: true })
      }
    }
    return points
  }, [operationResult, operationSteps, activeStepId])

  /** Keep the step's origin pin on the map even after a new result replaces mapPoints. */
  const activeStepOriginPoint = useMemo(() => {
    const src = activeOperationStep?.inputSource
    if (!src) return null
    if (src.kind === 'mapPoint') {
      return { point: src.point, fromStepId: activeStepId }
    }
    if (src.kind === 'priorPoint') {
      const point = findStepMapPoint(operationSteps, src.fromStepId, src.pointId)
      if (!point) return null
      return { point, fromStepId: src.fromStepId }
    }
    return null
  }, [activeOperationStep, operationSteps, activeStepId])

  const pointsForMapEntityPromotion = useMemo(() => {
    const list = [...displayedResultMapPoints]
    if (
      activeStepOriginPoint &&
      !list.some(({ point }) => point.id === activeStepOriginPoint.point.id)
    ) {
      list.push({
        point: activeStepOriginPoint.point,
        fromStepId: activeStepOriginPoint.fromStepId,
        interactive: false,
      })
    }
    return list
  }, [displayedResultMapPoints, activeStepOriginPoint])

  const displayedResultPointIds = useMemo(() => {
    const ids = new Set(displayedResultMapPoints.map(({ point }) => point.id))
    if (activeStepOriginPoint) ids.add(activeStepOriginPoint.point.id)
    if (userLocationPoint) ids.add(userLocationPoint.id)
    return ids
  }, [displayedResultMapPoints, activeStepOriginPoint, userLocationPoint])

  /**
   * Synthetic places only — entity-backed results render as live EntityMapMarkers.
   * Also keep unresolved entity-backed points (and the step origin) as result markers
   * so the origin never vanishes when a Nearby/etc. result takes over.
   */
  const syntheticResultMapPoints = useMemo(() => {
    const jobById = new Map(jobtreadJobs.map((j) => [j.id, j]))
    const personById = new Map(peopleWithJobLocations.map((p) => [p.id, p]))
    const assetById = new Map(linxupAssets.map((a) => [a.id, a]))
    const muniById = new Map(municipalityLicenses.map((m) => [m.id, m]))

    const canResolveSource = (point: OperationMapPoint): boolean => {
      const source = point.sourceEntity
      if (!source) return false
      if (source.category === 'job') {
        const job = jobById.get(source.id)
        return Boolean(job && Number.isFinite(job.lat) && Number.isFinite(job.lng))
      }
      if (source.category === 'person') {
        const person = personById.get(source.id)
        return Boolean(person && personHasMapPin(person))
      }
      if (source.category === 'asset') {
        const asset = assetById.get(source.id)
        return Boolean(asset && Number.isFinite(asset.lat) && Number.isFinite(asset.lng))
      }
      if (source.category === 'municipality') {
        const muni =
          muniById.get(source.id) ??
          externalMunicipalitiesById[source.id] ??
          (externalMunicipality?.id === source.id ? externalMunicipality : null)
        return Boolean(muni && Number.isFinite(muni.lat) && Number.isFinite(muni.lng))
      }
      return false
    }

    const entries = [...displayedResultMapPoints]
    if (
      operationResult &&
      activeStepOriginPoint &&
      !entries.some(({ point }) => point.id === activeStepOriginPoint.point.id)
    ) {
      entries.push({
        point: {
          ...activeStepOriginPoint.point,
          subtitle: activeStepOriginPoint.point.subtitle || 'Origin',
        },
        fromStepId: activeStepOriginPoint.fromStepId,
        interactive: false,
      })
    }

    return entries.filter(({ point }) => !point.sourceEntity || !canResolveSource(point))
  }, [
    displayedResultMapPoints,
    operationResult,
    activeStepOriginPoint,
    jobtreadJobs,
    peopleWithJobLocations,
    linxupAssets,
    municipalityLicenses,
    externalMunicipalitiesById,
    externalMunicipality,
  ])

  const mapEntitiesForDisplay = useMemo(() => {
    const entities = [...mapEntities]
    const seen = new Set(entities.map((e) => `${e.category}:${e.id}`))
    const jobById = new Map(jobtreadJobs.map((j) => [j.id, j]))
    const personById = new Map(peopleWithJobLocations.map((p) => [p.id, p]))
    const assetById = new Map(linxupAssets.map((a) => [a.id, a]))
    const muniById = new Map(municipalityLicenses.map((m) => [m.id, m]))
    const jobCoordKeys = new Set(
      filteredJobs.map((j) => `${j.lat.toFixed(5)},${j.lng.toFixed(5)}`),
    )

    for (const { point } of pointsForMapEntityPromotion) {
      const source = point.sourceEntity
      if (!source) continue
      const key = `${source.category}:${source.id}`
      if (seen.has(key)) continue
      seen.add(key)

      if (source.category === 'job') {
        const job = jobById.get(source.id)
        if (!job || !Number.isFinite(job.lat) || !Number.isFinite(job.lng)) continue
        entities.push({
          id: job.id,
          category: 'job',
          lat: job.lat,
          lng: job.lng,
          color: jobStatusColor(job.status),
          icon: faBuilding,
          title: job.name,
          subtitle: point.subtitle || titleCase(job.status || 'unknown'),
          detail: job.address || job.locationName || undefined,
        })
      } else if (source.category === 'person') {
        const person = personById.get(source.id)
        if (!person || !personHasMapPin(person)) continue
        const geo = normalizeLatLng(person.lat, person.lng)
        if (!geo) continue
        const besideJob = jobCoordKeys.has(`${geo.lat.toFixed(5)},${geo.lng.toFixed(5)}`)
        const [markerLat, markerLng] = personMarkerPosition(geo.lat, geo.lng, besideJob)
        entities.push({
          id: person.id,
          category: 'person',
          lat: markerLat,
          lng: markerLng,
          color: PERSON_KIND_COLOR[person.personKind],
          icon: faUser,
          title: person.name,
          subtitle: point.subtitle || personKindLabel(person.personKind),
          detail: person.jobName || undefined,
        })
      } else if (source.category === 'asset') {
        const asset = assetById.get(source.id)
        if (!asset || !Number.isFinite(asset.lat) || !Number.isFinite(asset.lng)) continue
        entities.push({
          id: asset.id,
          category: 'asset',
          lat: asset.lat,
          lng: asset.lng,
          color: ASSET_KIND_COLOR[asset.assetKind],
          icon: asset.assetKind === 'assetTracker' ? faSatelliteDish : faTruck,
          title: asset.name,
          subtitle: point.subtitle || asset.status,
          detail: asset.driver ? `Driver · ${asset.driver}` : undefined,
        })
      } else if (source.category === 'municipality') {
        const muni =
          muniById.get(source.id) ??
          externalMunicipalitiesById[source.id] ??
          (externalMunicipality?.id === source.id ? externalMunicipality : null)
        if (!muni || !Number.isFinite(muni.lat) || !Number.isFinite(muni.lng)) continue
        entities.push({
          id: muni.id,
          category: 'municipality',
          lat: muni.lat,
          lng: muni.lng,
          color: muni.isExternal
            ? EXTERNAL_MUNICIPALITY_COLOR
            : MUNICIPALITY_LEVEL_COLOR[muni.level],
          icon: faLandmark,
          title: muni.name,
          subtitle: point.subtitle || titleCase(muni.level),
          detail: muni.state || undefined,
        })
      }
    }

    return entities
  }, [
    mapEntities,
    pointsForMapEntityPromotion,
    jobtreadJobs,
    peopleWithJobLocations,
    linxupAssets,
    municipalityLicenses,
    filteredJobs,
    externalMunicipalitiesById,
    externalMunicipality,
  ])

  const clusterableMarkers = useMemo(() => {
    const markers: ClusterableMarker[] = mapEntitiesForDisplay.map((entity) => ({
      key: `entity:${entity.category}:${entity.id}`,
      lat: entity.lat,
      lng: entity.lng,
      title: entity.title,
      subtitle: entity.subtitle,
      color: entity.color,
    }))
    for (const { point, fromStepId } of syntheticResultMapPoints) {
      markers.push({
        key: `result:${fromStepId}:${point.id}`,
        lat: point.lat,
        lng: point.lng,
        title: point.name,
        subtitle: point.subtitle,
        color: point.color,
      })
    }
    return markers
  }, [mapEntitiesForDisplay, syntheticResultMapPoints])

  const clusterExcludeKeys = useMemo(() => {
    const keys = new Set<string>()
    if (focusId) {
      const sel = omniSelections.find((s) => s.id === focusId)
      if (sel) keys.add(`entity:${sel.category}:${focusId}`)
    }
    if (focusedResultPointId) {
      const entry =
        displayedResultMapPoints.find(({ point }) => point.id === focusedResultPointId) ??
        (activeStepOriginPoint?.point.id === focusedResultPointId
          ? activeStepOriginPoint
          : null)
      if (entry) keys.add(`result:${entry.fromStepId}:${focusedResultPointId}`)
    }
    // Same-location piles must stay clustered so you can pick another member
    // without unpinning the current selection.
    for (const key of [...keys]) {
      if (hasCoincidentNeighbor(clusterableMarkers, key)) keys.delete(key)
    }
    return keys
  }, [
    focusId,
    focusedResultPointId,
    omniSelections,
    displayedResultMapPoints,
    activeStepOriginPoint,
    clusterableMarkers,
  ])

  const placeOperators = useMemo(
    () =>
      getAvailableOperators(
        operationEntityFromMapPoint({
          id: 'place',
          name: 'Place',
          lat: 0,
          lng: 0,
          color: '#000',
        }).capabilities,
        { enabledOperators: operationConfig.enabledOperators },
      ),
    [operationConfig.enabledOperators],
  )

  const focusedResultPoint = useMemo(() => {
    if (!focusedResultPointId) return null
    return (
      displayedResultMapPoints.find(({ point }) => point.id === focusedResultPointId)?.point ??
      (activeStepOriginPoint?.point.id === focusedResultPointId
        ? activeStepOriginPoint.point
        : null) ??
      (userLocationPoint?.id === focusedResultPointId ? userLocationPoint : null)
    )
  }, [focusedResultPointId, displayedResultMapPoints, activeStepOriginPoint, userLocationPoint])

  const resultEntityFocus = useMemo(
    () => focusedResultPoint?.sourceEntity ?? null,
    [focusedResultPoint],
  )

  const focusedMapEntity = useMemo(() => {
    if (focusedEntity) return focusedEntity
    if (!focusId) return null
    return mapEntitiesForDisplay.find((e) => e.id === focusId) ?? null
  }, [focusedEntity, focusId, mapEntitiesForDisplay])

  useEffect(() => {
    if (!focusedResultPointId) return
    if (!displayedResultPointIds.has(focusedResultPointId)) {
      setFocusedResultPointId(null)
    }
  }, [displayedResultPointIds, focusedResultPointId])

  const focusResultPoint = useCallback(
    (pointId: string) => {
      setFocusedResultPointId(pointId)
      setResultFocusToken((n) => n + 1)
      if (isMobile) setOutputOpen(true)
      else nudgeCollapsedOutputBadge()

      const entry =
        displayedResultMapPoints.find(({ point }) => point.id === pointId) ??
        (activeStepOriginPoint?.point.id === pointId
          ? {
              point: activeStepOriginPoint.point,
              fromStepId: activeStepOriginPoint.fromStepId,
              interactive: false,
            }
          : null) ??
        (userLocationPoint?.id === pointId
          ? {
              point: userLocationPoint,
              fromStepId: '',
              interactive: false,
            }
          : null)
      if (!entry) return

      if (entry.interactive) {
        setOperationSteps((prev) =>
          prev.map((step) =>
            step.id === activeStepId
              ? {
                  ...step,
                  inputSource: {
                    kind: 'priorPoint',
                    fromStepId: entry.fromStepId,
                    pointId,
                  },
                  operatorId: '' as const,
                  inputs: { ...DEFAULT_OPERATION_INPUTS },
                  result: null,
                }
              : step,
          ),
        )
      }

      const source = entry.point.sourceEntity
      if (!source) {
        setFocusId(null)
        return
      }

      setOmniSelections((prev) => {
        if (prev.some((s) => s.id === source.id && s.category === source.category)) return prev
        return [...prev, { id: source.id, category: source.category }]
      })
      setFocusId(source.id)
      setEntityFocusToken((n) => n + 1)
      if (source.category === 'municipality') {
        const ext = externalMunicipalitiesById[source.id]
        if (ext) setExternalMunicipality(ext)
      }
    },
    [
      displayedResultMapPoints,
      activeStepOriginPoint,
      activeStepId,
      externalMunicipalitiesById,
      userLocationPoint,
      isMobile,
      nudgeCollapsedOutputBadge,
    ],
  )

  const clearMyLocation = useCallback(() => {
    setUserLocationPoint(null)
    setLocateError(null)
    setFocusedResultPointId((id) => (id === USER_LOCATION_POINT_ID ? null : id))
  }, [])

  const useMyLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocateError('Geolocation is not supported in this browser.')
      return
    }
    setLocateError(null)
    setLocatingMe(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const point: OperationMapPoint = {
          id: USER_LOCATION_POINT_ID,
          name: 'My location',
          subtitle: 'Current GPS position',
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          color: USER_LOCATION_COLOR,
        }
        setUserLocationPoint(point)
        setLocatingMe(false)
        setFocusId(null)
        setFocusedResultPointId(point.id)
        setResultFocusToken((n) => n + 1)
        if (isMobile) setOutputOpen(true)
      },
      (err) => {
        setLocatingMe(false)
        const message =
          err.code === err.PERMISSION_DENIED
            ? 'Location permission denied.'
            : err.code === err.POSITION_UNAVAILABLE
              ? 'Location unavailable.'
              : err.code === err.TIMEOUT
                ? 'Location request timed out.'
                : 'Could not get your location.'
        setLocateError(message)
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 15000 },
    )
  }, [isMobile])

  const pickPriorPointForActiveStep = useCallback(
    (fromStepId: string, pointId: string) => {
      const from = operationSteps.find((s) => s.id === fromStepId)
      const point = from?.result?.mapPoints.find((p) => p.id === pointId)
      if (!point) return

      setOperationSteps((prev) =>
        prev.map((step) =>
          step.id === activeStepId
            ? {
                ...step,
                inputSource: { kind: 'priorPoint', fromStepId, pointId },
                operatorId: '' as const,
                inputs: { ...DEFAULT_OPERATION_INPUTS },
                result: null,
              }
            : step,
        ),
      )

      setFocusedResultPointId(pointId)
      setResultFocusToken((n) => n + 1)

      const source = point.sourceEntity
      if (!source) return

      setOmniSelections((prev) => {
        if (prev.some((s) => s.id === source.id && s.category === source.category)) return prev
        return [...prev, { id: source.id, category: source.category }]
      })
      setFocusId(source.id)
      if (source.category === 'municipality') {
        const ext = externalMunicipalitiesById[source.id]
        if (ext) setExternalMunicipality(ext)
      }
    },
    [operationSteps, activeStepId, externalMunicipalitiesById],
  )

  const selectEntityFromMapForChain = useCallback(
    (id: string, category: EntityCategory) => {
      selectEntityFromMap(id, category)
      const match = displayedResultMapPoints.find(
        (entry) =>
          entry.interactive &&
          entry.point.sourceEntity?.id === id &&
          entry.point.sourceEntity.category === category,
      )
      if (!match) return
      pickPriorPointForActiveStep(match.fromStepId, match.point.id)
    },
    [selectEntityFromMap, displayedResultMapPoints, pickPriorPointForActiveStep],
  )

  const selectClusterMember = useCallback(
    (markerKey: string) => {
      if (markerKey.startsWith('entity:')) {
        const rest = markerKey.slice('entity:'.length)
        const sep = rest.indexOf(':')
        if (sep <= 0) return
        const category = rest.slice(0, sep) as EntityCategory
        const id = rest.slice(sep + 1)
        if (!id) return
        selectEntityFromMapForChain(id, category)
        return
      }
      if (markerKey.startsWith('result:')) {
        const rest = markerKey.slice('result:'.length)
        const sep = rest.indexOf(':')
        if (sep < 0) return
        const pointId = rest.slice(sep + 1)
        if (!pointId) return
        focusResultPoint(pointId)
      }
    },
    [selectEntityFromMapForChain, focusResultPoint],
  )

  const handleSelectStep = (stepId: string) => {
    setActiveStepId(stepId)
  }

  const handleAddStep = () => {
    const fresh = createEmptyOperationStep()
    setOperationSteps((prev) => [...prev, fresh])
    setActiveStepId(fresh.id)
  }

  const handleRemoveStep = (stepId: string) => {
    setOperationSteps((prev) => {
      if (prev.length <= 1) return prev
      const idx = prev.findIndex((s) => s.id === stepId)
      if (idx < 0) return prev
      const next = prev
        .filter((s) => s.id !== stepId)
        .map((step) => {
          if (step.inputSource?.kind === 'priorPoint' && step.inputSource.fromStepId === stepId) {
            return {
              ...step,
              inputSource: null,
              operatorId: '' as const,
              inputs: { ...DEFAULT_OPERATION_INPUTS },
              result: null,
            }
          }
          return step
        })
      if (activeStepId === stepId) {
        const neighbor = next[Math.max(0, idx - 1)] ?? next[0]
        setActiveStepId(neighbor.id)
      }
      return next
    })
  }

  // Drop priorPoint inputs that no longer resolve after a prior result changes.
  useEffect(() => {
    setOperationSteps((prev) => {
      let changed = false
      const next = prev.map((step) => {
        if (step.inputSource?.kind !== 'priorPoint') return step
        const { fromStepId, pointId } = step.inputSource
        const from = prev.find((s) => s.id === fromStepId)
        const stillThere = from?.result?.mapPoints.some((p) => p.id === pointId)
        if (stillThere) return step
        changed = true
        return {
          ...step,
          inputSource: null,
          operatorId: '' as const,
          inputs: { ...DEFAULT_OPERATION_INPUTS },
          result: null,
        }
      })
      return changed ? next : prev
    })
  }, [operationSteps.map((s) => `${s.id}:${s.result?.ranAt ?? 0}`).join('|')])

  const handleShareOperation = async () => {
    const text = formatOperationStepsShareText(operationSteps, activeStepId)
    if (!text) return
    const ok = await copyTextToClipboard(text)
    if (!ok) return
    setShareCopied(true)
    if (shareCopiedTimeoutRef.current) clearTimeout(shareCopiedTimeoutRef.current)
    shareCopiedTimeoutRef.current = setTimeout(() => setShareCopied(false), 2000)
  }

  useEffect(() => {
    return () => {
      if (shareCopiedTimeoutRef.current) clearTimeout(shareCopiedTimeoutRef.current)
    }
  }, [])

  const resolveMunicipalityById = useCallback(
    (id: string): ControlCenterMunicipality | null => {
      return (
        municipalityLicenses.find((m) => m.id === id) ??
        externalMunicipalitiesById[id] ??
        (externalMunicipality?.id === id ? externalMunicipality : null)
      )
    },
    [municipalityLicenses, externalMunicipalitiesById, externalMunicipality],
  )

  const municipalitiesForOperations = useMemo(() => {
    const byId = new Map(municipalityLicenses.map((m) => [m.id, m]))
    for (const m of Object.values(externalMunicipalitiesById)) {
      byId.set(m.id, m)
    }
    if (externalMunicipality) byId.set(externalMunicipality.id, externalMunicipality)
    return [...byId.values()]
  }, [municipalityLicenses, externalMunicipalitiesById, externalMunicipality])

  // Clear selection-backed results when the parent entity disappears from catalogs.
  useEffect(() => {
    setOperationSteps((prev) =>
      clearStaleSelectionOperationResults(prev, {
        jobs: jobtreadJobs,
        people: peopleWithJobLocations,
        assets: linxupAssets,
        municipalities: municipalitiesForOperations,
      }),
    )
  }, [jobtreadJobs, peopleWithJobLocations, linxupAssets, municipalitiesForOperations])

  const resolveOperationEntityForMapEntity = useCallback(
    (entity: MappableEntity): OperationEntity | null => {
      if (entity.category === 'job') {
        const job = jobtreadJobs.find((j) => j.id === entity.id)
        return job ? operationEntityFromJob(job) : null
      }
      if (entity.category === 'person') {
        const person = peopleWithJobLocations.find((p) => p.id === entity.id)
        if (!person) return null
        return operationEntityFromPerson(person) ?? operationEntityFromPersonIdentity(person)
      }
      if (entity.category === 'asset') {
        const asset = linxupAssets.find((a) => a.id === entity.id)
        return asset ? operationEntityFromAsset(asset) : null
      }
      if (entity.category === 'municipality') {
        const muni = resolveMunicipalityById(entity.id)
        return muni ? operationEntityFromMunicipality(muni) : null
      }
      return null
    },
    [jobtreadJobs, peopleWithJobLocations, linxupAssets, resolveMunicipalityById],
  )

  const operatorsForEntityId = useMemo(() => {
    const map = new Map<string, OperatorDef[]>()
    for (const entity of mapEntitiesForDisplay) {
      const opEntity = resolveOperationEntityForMapEntity(entity)
      map.set(
        entity.id,
        opEntity
          ? getAvailableOperators(opEntity.capabilities, {
              enabledOperators: operationConfig.enabledOperators,
            })
          : [],
      )
    }
    return map
  }, [mapEntitiesForDisplay, resolveOperationEntityForMapEntity, operationConfig.enabledOperators])

  const pickOmniDestinationRef = useCallback(
    (primaryId: string): string => {
      for (const sel of omniSelections) {
        if (sel.id === primaryId) continue
        if (sel.category === 'job') {
          const job = jobtreadJobs.find((j) => j.id === sel.id)
          if (job && Number.isFinite(job.lat) && Number.isFinite(job.lng)) {
            return entityRef('job', job.id)
          }
        } else if (sel.category === 'person') {
          const person = peopleWithJobLocations.find((p) => p.id === sel.id)
          if (person && personHasMapPin(person)) {
            return entityRef('person', person.id)
          }
        } else if (sel.category === 'asset') {
          const asset = linxupAssets.find((a) => a.id === sel.id)
          if (asset && Number.isFinite(asset.lat) && Number.isFinite(asset.lng)) {
            return entityRef('asset', asset.id)
          }
        } else if (sel.category === 'municipality') {
          const muni = resolveMunicipalityById(sel.id)
          if (muni && Number.isFinite(muni.lat) && Number.isFinite(muni.lng)) {
            return entityRef('municipality', muni.id)
          }
        }
      }
      return ''
    },
    [
      omniSelections,
      jobtreadJobs,
      peopleWithJobLocations,
      linxupAssets,
      resolveMunicipalityById,
    ],
  )

  const handlePopupOperatorInputsEdit = useCallback(
    (inputs: OperationInputs) => {
      // Invalidate any in-flight run so a stale result can't overwrite the edit.
      operationRunGenRef.current += 1
      setOperationRunning(false)
      setOperationSteps((prev) =>
        prev.map((step) =>
          step.id === activeStepId
            ? {
                ...step,
                inputs,
                result: null,
              }
            : step,
        ),
      )
    },
    [activeStepId],
  )

  /** Append a new chain step, or patch the active step in place. Returns the step id that will receive the result. */
  const commitOperatorStep = useCallback(
    (args: {
      append: boolean
      operatorId: OperatorId
      inputs: OperationInputs
      inputSource: OperationStepInputSource
    }): string => {
      const { append, operatorId, inputs, inputSource } = args
      if (append) {
        const fresh = createEmptyOperationStep()
        setOperationSteps((prev) => [
          ...prev,
          {
            ...fresh,
            operatorId,
            inputSource,
            inputs,
            result: null,
          },
        ])
        setActiveStepId(fresh.id)
        return fresh.id
      }
      setOperationSteps((prev) =>
        prev.map((step) =>
          step.id === activeStepId
            ? {
                ...step,
                operatorId,
                inputSource,
                inputs,
                result: null,
              }
            : step,
        ),
      )
      return activeStepId
    },
    [activeStepId],
  )

  const executeOperator = useCallback(
    async (args: {
      operatorId: OperatorId
      primary: OperationEntity
      inputs: OperationInputs
      municipalityBoundary?: TigerwebFeatureCollection | null
    }): Promise<OperationResult | null> => {
      const { operatorId, primary, inputs, municipalityBoundary = null } = args
      if (operatorId === 'eta') {
        return runEtaOperation({
          primary,
          inputs,
          jobs: jobtreadJobs,
          people: peopleWithJobLocations,
          assets: linxupAssets,
          municipalities: municipalitiesForOperations,
          steps: operationSteps,
        })
      }
      if (operatorId === 'weather-forecast') {
        return runWeatherForecastOperation({ primary })
      }
      if (operatorId === 'time') {
        return runTimeOperation({ primary, inputs })
      }
      if (operatorId === 'cert-check') {
        return runCertCheckOperation({ primary })
      }
      if (operatorId === 'local-license-check') {
        return runLocalLicenseCheckOperation({
          primary,
          municipalities: municipalitiesForOperations,
        })
      }
      if (
        operatorId === 'nearby' &&
        inputs.nearbyTarget &&
        !isNearbyLayerTarget(inputs.nearbyTarget)
      ) {
        return runNearbyPlacesOperation({ primary, inputs })
      }
      return runOperation({
        operatorId,
        primary,
        inputs,
        jobs: jobtreadJobs,
        people: peopleWithJobLocations,
        assets: linxupAssets,
        municipalities: municipalitiesForOperations,
        steps: operationSteps,
        municipalityBoundary,
      })
    },
    [
      jobtreadJobs,
      peopleWithJobLocations,
      linxupAssets,
      municipalitiesForOperations,
      operationSteps,
    ],
  )

  const writeOperatorResult = useCallback(
    (
      stepId: string,
      runGen: number,
      primary: OperationEntity,
      operatorId: OperatorId,
      result: OperationResult | null,
      err?: unknown,
    ) => {
      if (runGen !== operationRunGenRef.current) return
      if (err != null) {
        const message = err instanceof Error ? err.message : 'Operation failed'
        const errorResult: OperationResult = {
          operatorId,
          title: operatorLabel(operatorId) || 'Operation failed',
          summary: message,
          entityId: primary.id,
          entityName: primary.name,
          ranAt: Date.now(),
          rows: [{ label: 'Error', value: message }],
          mapPoints: [],
          routeLine: null,
        }
        setOperationSteps((prev) =>
          prev.map((step) => (step.id === stepId ? { ...step, result: errorResult } : step)),
        )
        return
      }
      if (!result) return
      setOperationSteps((prev) =>
        prev.map((step) => (step.id === stepId ? { ...step, result } : step)),
      )
    },
    [],
  )

  const runOmniOperatorFromPopup = useCallback(
    async (
      entity: MappableEntity,
      nextOperatorId: OperatorId,
      inputs: OperationInputs,
    ) => {
      const primary = resolveOperationEntityForMapEntity(entity)
      if (!primary) return

      const runGen = ++operationRunGenRef.current
      setFocusId(entity.id)
      setOutputOpen(true)
      setOperationRunning(true)

      const opDef = OPERATORS.find((op) => op.id === nextOperatorId)
      if (opDef && !areOperatorArgsComplete(opDef, inputs)) {
        if (runGen === operationRunGenRef.current) setOperationRunning(false)
        return
      }

      let municipalityBoundary: TigerwebFeatureCollection | null = null
      if (
        primary.kind === 'municipality' &&
        primary.municipality?.censusGeoid &&
        isTigerwebBoundaryLevel(primary.municipality.level)
      ) {
        try {
          if (boundaryForId === primary.id && boundaryGeojson) {
            municipalityBoundary = boundaryGeojson
          } else {
            municipalityBoundary = await fetchTigerwebBoundary(
              primary.municipality.level,
              primary.municipality.censusGeoid,
            )
            if (runGen !== operationRunGenRef.current) return
            setBoundaryGeojson(municipalityBoundary)
            setBoundaryForId(primary.id)
          }
        } catch {
          municipalityBoundary = null
        }
      }

      if (runGen !== operationRunGenRef.current) return

      const active = operationSteps.find((s) => s.id === activeStepId)
      let inputSource: OperationStepInputSource = {
        kind: 'selection',
        category: entity.category as OmniCategory,
      }
      const priorInput =
        active?.inputSource?.kind === 'priorPoint' ? active.inputSource : null
      if (priorInput) {
        const from = operationSteps.find((s) => s.id === priorInput.fromStepId)
        const point = from?.result?.mapPoints.find((p) => p.id === priorInput.pointId)
        if (point?.sourceEntity?.id === entity.id) {
          inputSource = priorInput
        }
      } else if (
        active?.inputSource?.kind === 'mapPoint' &&
        active.inputSource.point.sourceEntity?.id === entity.id
      ) {
        inputSource = active.inputSource
      }

      const resultPoint = active?.result?.mapPoints.find(
        (p) =>
          p.sourceEntity?.id === entity.id &&
          p.sourceEntity.category === entity.category,
      )
      const isOriginEntity =
        (operationPrimary != null &&
          operationPrimary.kind !== 'place' &&
          operationPrimary.id === entity.id) ||
        (activeStepOriginPoint?.point.sourceEntity?.id === entity.id &&
          activeStepOriginPoint.point.sourceEntity.category === entity.category)
      const destUsesCurrentResult = destinationTargetsStepResult(inputs, active)
      const opUsesDestination = Boolean(opDef?.args.includes('destination'))

      // Operating on a pin from the active step's result → append a new chain step.
      // Same when ETA (etc.) runs from the origin but destinations a current result pin.
      const append =
        Boolean(active?.result && resultPoint && !isOriginEntity) ||
        Boolean(
          active?.result && isOriginEntity && opUsesDestination && destUsesCurrentResult,
        )
      if (active?.result && resultPoint && !isOriginEntity) {
        inputSource = {
          kind: 'priorPoint',
          fromStepId: active.id,
          pointId: resultPoint.id,
        }
      }

      const stepId = commitOperatorStep({
        append,
        operatorId: nextOperatorId,
        inputs,
        inputSource,
      })

      try {
        const result = await executeOperator({
          operatorId: nextOperatorId,
          primary,
          inputs,
          municipalityBoundary,
        })
        writeOperatorResult(stepId, runGen, primary, nextOperatorId, result)
      } catch (err: unknown) {
        writeOperatorResult(stepId, runGen, primary, nextOperatorId, null, err)
      } finally {
        if (runGen === operationRunGenRef.current) {
          setOperationRunning(false)
        }
      }
    },
    [
      resolveOperationEntityForMapEntity,
      activeStepId,
      operationSteps,
      operationPrimary,
      activeStepOriginPoint,
      boundaryForId,
      boundaryGeojson,
      commitOperatorStep,
      executeOperator,
      writeOperatorResult,
    ],
  )

  const runOperatorFromResultMapPoint = useCallback(
    async (
      point: OperationMapPoint,
      nextOperatorId: OperatorId,
      inputs: OperationInputs,
    ) => {
      const entry =
        displayedResultMapPoints.find((e) => e.point.id === point.id) ??
        (activeStepOriginPoint?.point.id === point.id
          ? {
              point: activeStepOriginPoint.point,
              fromStepId: activeStepOriginPoint.fromStepId,
              interactive: false as boolean,
            }
          : null) ??
        (userLocationPoint?.id === point.id
          ? {
              point: userLocationPoint,
              fromStepId: '',
              interactive: false as boolean,
            }
          : null)

      const primary = operationEntityFromMapPoint(point)
      const active = operationSteps.find((s) => s.id === activeStepId)
      const inCurrentResult = Boolean(
        active?.result?.mapPoints.some((p) => p.id === point.id),
      )
      const isOrigin = activeStepOriginPoint?.point.id === point.id

      let inputSource: OperationStepInputSource
      if (active?.result && inCurrentResult && !isOrigin) {
        inputSource = {
          kind: 'priorPoint',
          fromStepId: active.id,
          pointId: point.id,
        }
      } else if (entry && entry.interactive && entry.fromStepId !== activeStepId) {
        inputSource = {
          kind: 'priorPoint',
          fromStepId: entry.fromStepId,
          pointId: point.id,
        }
      } else {
        inputSource = { kind: 'mapPoint', point: { ...point } }
      }

      const runGen = ++operationRunGenRef.current
      setOutputOpen(true)
      setOperationRunning(true)
      setFocusedResultPointId(point.id)

      const opDef = OPERATORS.find((op) => op.id === nextOperatorId)
      if (opDef && !areOperatorArgsComplete(opDef, inputs)) {
        if (runGen === operationRunGenRef.current) setOperationRunning(false)
        return
      }

      const destUsesCurrentResult = destinationTargetsStepResult(inputs, active)
      const opUsesDestination = Boolean(opDef?.args.includes('destination'))
      const append =
        Boolean(active?.result && inCurrentResult && !isOrigin) ||
        Boolean(active?.result && isOrigin && opUsesDestination && destUsesCurrentResult)

      const stepId = commitOperatorStep({
        append,
        operatorId: nextOperatorId,
        inputs,
        inputSource,
      })

      try {
        const result = await executeOperator({
          operatorId: nextOperatorId,
          primary,
          inputs,
        })
        writeOperatorResult(stepId, runGen, primary, nextOperatorId, result)
      } catch (err: unknown) {
        writeOperatorResult(stepId, runGen, primary, nextOperatorId, null, err)
      } finally {
        if (runGen === operationRunGenRef.current) {
          setOperationRunning(false)
        }
      }
    },
    [
      displayedResultMapPoints,
      activeStepOriginPoint,
      activeStepId,
      operationSteps,
      commitOperatorStep,
      executeOperator,
      writeOperatorResult,
      userLocationPoint,
    ],
  )

  const popupDestinationOptions = useMemo(() => {
    const options: Array<{ id: string; label: string; group: string; keywords?: string }> = []

    const activeIdx = Math.max(
      0,
      operationSteps.findIndex((s) => s.id === activeStepId),
    )
    // Active-step pins first so Nearby gas stations (etc.) are searchable for ETA.
    options.push(
      ...destinationOptionsFromStepResults(operationSteps, activeIdx, activeStepId),
    )

    if (userLocationPoint) {
      options.unshift({
        id: `${userLocationPoint.lat},${userLocationPoint.lng}`,
        label: 'My location',
        group: 'Places',
        keywords: 'gps current location here me',
      })
    }

    for (const j of jobtreadJobs) {
      if (!Number.isFinite(j.lat) || !Number.isFinite(j.lng)) continue
      options.push({
        id: entityRef('job', j.id),
        label: j.name,
        group: 'Jobs',
        keywords: [j.name, j.number, j.status].filter(Boolean).join(' '),
      })
    }
    for (const p of peopleWithJobLocations) {
      if (!personHasMapPin(p)) continue
      options.push({
        id: entityRef('person', p.id),
        label: p.name,
        group: 'People',
        keywords: [p.name, personKindLabel(p.personKind)].join(' '),
      })
    }
    for (const a of linxupAssets) {
      if (!Number.isFinite(a.lat) || !Number.isFinite(a.lng)) continue
      options.push({
        id: entityRef('asset', a.id),
        label: a.name,
        group: 'Assets',
        keywords: [a.name, assetKindLabel(a.assetKind), a.status].join(' '),
      })
    }
    for (const m of municipalitiesForOperations) {
      if (!Number.isFinite(m.lat) || !Number.isFinite(m.lng)) continue
      options.push({
        id: entityRef('municipality', m.id),
        label: m.isExternal ? `${m.name} (not in DB)` : m.name,
        group: 'Municipalities',
        keywords: [m.name, m.state, m.level, m.isExternal ? 'unlicensed census' : ''].join(' '),
      })
    }
    return options
  }, [
    operationSteps,
    activeStepId,
    jobtreadJobs,
    peopleWithJobLocations,
    linxupAssets,
    municipalitiesForOperations,
    userLocationPoint,
  ])

  const resultMarkerIcon = (point: OperationMapPoint): IconDefinition => {
    if (point.id === USER_LOCATION_POINT_ID) return faLocationCrosshairs
    if (
      operationResult?.operatorId === 'fuel-planning' &&
      point.id.startsWith('fuel')
    ) {
      return faGasPump
    }
    if (point.id.startsWith('place-')) {
      const target = operationInputs.nearbyTarget
      if (target === 'gas_station') return faGasPump
      return faMapPin
    }
    if (point.id.startsWith('dest-')) return faMapPin
    return faMapPin
  }

  const operationOrigin = operationPrimary
    ? { lat: operationPrimary.lat, lng: operationPrimary.lng }
    : null

  useEffect(() => {
    if (focusId && !selectedIds.has(focusId)) {
      setFocusId(null)
    }
  }, [focusId, selectedIds])

  /** Only the focused municipality should drive boundary load/display. */
  const municipalityForBoundary = useMemo((): ControlCenterMunicipality | null => {
    if (!focusId) return null
    const focusedMuni = omniSelections.some(
      (s) => s.category === 'municipality' && s.id === focusId,
    )
    if (!focusedMuni) return null
    return resolveMunicipalityById(focusId)
  }, [focusId, omniSelections, resolveMunicipalityById])

  useEffect(() => {
    if (
      !municipalityForBoundary ||
      !municipalityForBoundary.censusGeoid ||
      !isTigerwebBoundaryLevel(municipalityForBoundary.level)
    ) {
      setBoundaryLoading(false)
      return
    }

    // Already showing this municipality's area — keep it (e.g. census select
    // that just set boundary from the same fetch used for the centroid).
    if (
      boundaryForId === municipalityForBoundary.id &&
      boundaryGeojson &&
      !boundaryLoading
    ) {
      return
    }

    const requestId = ++boundaryRequestIdRef.current
    const municipalityId = municipalityForBoundary.id
    const level = municipalityForBoundary.level
    const geoid = municipalityForBoundary.censusGeoid
    const controller = new AbortController()
    setBoundaryLoading(true)
    setBoundaryGeojson(null)
    setBoundaryForId(null)

    fetchTigerwebBoundary(level, geoid, {
      signal: controller.signal,
    })
      .then((fc) => {
        if (controller.signal.aborted) return
        if (boundaryRequestIdRef.current !== requestId) return
        setBoundaryGeojson(fc)
        setBoundaryForId(municipalityId)
        setBoundaryLoading(false)
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (boundaryRequestIdRef.current !== requestId) return
        setBoundaryGeojson(null)
        setBoundaryForId(null)
        setBoundaryLoading(false)
      })

    return () => controller.abort()
  }, [
    municipalityForBoundary?.id,
    municipalityForBoundary?.censusGeoid,
    municipalityForBoundary?.level,
    // Re-run when focus returns to a municipality after visiting something else.
    focusId,
  ])

  const activeBoundary =
    municipalityForBoundary && boundaryForId === municipalityForBoundary.id
      ? boundaryGeojson
      : null
  const focusingMunicipality = Boolean(
    municipalityForBoundary && focusId === municipalityForBoundary.id,
  )
  const municipalityExpectsBoundary = Boolean(
    focusingMunicipality &&
      municipalityForBoundary?.censusGeoid &&
      isTigerwebBoundaryLevel(municipalityForBoundary.level),
  )
  const useMarkerFlyTo = Boolean(
    focusedEntity && !municipalityExpectsBoundary && !isDisplayKiosk,
  )

  /** Docking ops (nearby/contains/forecast/eta) apply only to the root that ran them. */
  const lookAroundDockOperatorId = useMemo((): OperatorId | null => {
    const opId = operationResult?.operatorId ?? null
    return operatorDocksPopupToCorner(opId) ? opId : null
  }, [operationResult?.operatorId])

  const isLookAroundRootEntity = useCallback(
    (entity: { id: string; category: string }): boolean => {
      if (!lookAroundDockOperatorId) return false
      // Same origin test as omni run-from-popup (`isOriginEntity`).
      return (
        (operationPrimary != null &&
          operationPrimary.kind !== 'place' &&
          operationPrimary.id === entity.id) ||
        (activeStepOriginPoint?.point.sourceEntity?.id === entity.id &&
          activeStepOriginPoint.point.sourceEntity.category === entity.category)
      )
    },
    [lookAroundDockOperatorId, operationPrimary, activeStepOriginPoint],
  )

  const mapFocusAnchor = useMemo(
    () => (isMobile && outputOpen ? { x: 0.5, y: 0.25 } : undefined),
    [isMobile, outputOpen],
  )
  const prevOutputOpenRef = useRef(outputOpen)
  const [panelOpenFocusToken, setPanelOpenFocusToken] = useState(0)
  useEffect(() => {
    const justOpened = outputOpen && !prevOutputOpenRef.current
    prevOutputOpenRef.current = outputOpen
    if (isMobile && justOpened) setPanelOpenFocusToken((n) => n + 1)
  }, [isMobile, outputOpen])

  const canShareOperation = Boolean(
    formatOperationStepsShareText(operationSteps, activeStepId),
  )

  const inlineEntityOperations = useMemo(() => {
    if (!isMobile || !focusId) return null

    const mapEntity =
      mapEntitiesForDisplay.find((e) => e.id === focusId) ??
      (selectedPerson &&
      selectedPerson.id === focusId &&
      !personHasMapPin(selectedPerson)
        ? ({
            id: selectedPerson.id,
            category: 'person' as const,
            lat: Number.NaN,
            lng: Number.NaN,
            color: PERSON_KIND_COLOR[selectedPerson.personKind],
            icon: faUser,
            title: selectedPerson.name,
            subtitle: [
              personKindLabel(selectedPerson.personKind),
              selectedPerson.clockedIn ? 'Clocked in' : 'Off clock',
              'No map location',
            ].join(' · '),
          } satisfies MappableEntity)
        : null)

    if (!mapEntity) return null

    let operators = operatorsForEntityId.get(focusId) ?? []
    if (operators.length === 0 && selectedPerson?.id === focusId) {
      const opEntity =
        operationEntityFromPerson(selectedPerson) ??
        operationEntityFromPersonIdentity(selectedPerson)
      operators = opEntity
        ? getAvailableOperators(opEntity.capabilities, {
            enabledOperators: operationConfig.enabledOperators,
          })
        : []
    }
    if (operators.length === 0) return null

    return {
      targetId: focusId,
      variant: 'entity' as const,
      operators,
      destinationOptions: popupDestinationOptions.filter(
        (opt) => opt.id !== entityRef(mapEntity.category as OmniCategory, mapEntity.id),
      ),
      preferredDestinationRef: pickOmniDestinationRef(focusId),
      operationResultOperatorId: operationResult?.operatorId ?? null,
      openFocusToken: entityFocusToken,
      operationRunning,
      onRun: (op: OperatorDef, inputs: OperationInputs) => {
        void runOmniOperatorFromPopup(mapEntity, op.id, inputs)
      },
    }
  }, [
    isMobile,
    focusId,
    mapEntitiesForDisplay,
    selectedPerson,
    operatorsForEntityId,
    operationConfig.enabledOperators,
    popupDestinationOptions,
    pickOmniDestinationRef,
    operationResult?.operatorId,
    entityFocusToken,
    operationRunning,
    runOmniOperatorFromPopup,
  ])

  const myLocationPlace = useMemo(() => {
    if (!userLocationPoint) return null
    const focused = focusedResultPointId === userLocationPoint.id
    return {
      point: userLocationPoint,
      focused,
      operations:
        focused && isMobile
          ? {
              targetId: userLocationPoint.id,
              variant: 'result' as const,
              operators: placeOperators,
              destinationOptions: popupDestinationOptions.filter(
                (opt) =>
                  opt.id !== `${userLocationPoint.lat},${userLocationPoint.lng}`,
              ),
              preferredDestinationRef: '',
              operationResultOperatorId: operationResult?.operatorId ?? null,
              openFocusToken: resultFocusToken,
              operationRunning,
              onRun: (op: OperatorDef, inputs: OperationInputs) => {
                void runOperatorFromResultMapPoint(userLocationPoint, op.id, inputs)
              },
            }
          : null,
    }
  }, [
    userLocationPoint,
    focusedResultPointId,
    isMobile,
    placeOperators,
    popupDestinationOptions,
    operationResult?.operatorId,
    resultFocusToken,
    operationRunning,
    runOperatorFromResultMapPoint,
  ])

  /** Transient map-result places (e.g. Nearby pins) — only while focused. */
  const focusedPlace = useMemo(() => {
    if (!isMobile || !focusedResultPoint || focusedResultPoint.sourceEntity) return null
    if (userLocationPoint && focusedResultPoint.id === userLocationPoint.id) return null
    if (focusId && resultEntityFocus) return null
    return {
      point: focusedResultPoint,
      operations: {
        targetId: focusedResultPoint.id,
        variant: 'result' as const,
        operators: placeOperators,
        destinationOptions: popupDestinationOptions.filter(
          (opt) =>
            opt.id !== `${focusedResultPoint.lat},${focusedResultPoint.lng}`,
        ),
        preferredDestinationRef: '',
        operationResultOperatorId: operationResult?.operatorId ?? null,
        openFocusToken: resultFocusToken,
        operationRunning,
        onRun: (op: OperatorDef, inputs: OperationInputs) => {
          void runOperatorFromResultMapPoint(focusedResultPoint, op.id, inputs)
        },
      },
    }
  }, [
    isMobile,
    focusedResultPoint,
    userLocationPoint,
    focusId,
    resultEntityFocus,
    placeOperators,
    popupDestinationOptions,
    operationResult?.operatorId,
    resultFocusToken,
    operationRunning,
    runOperatorFromResultMapPoint,
  ])

  const outputPanelProps = {
    pinnedJobs,
    pinnedPeople,
    pinnedAssets,
    pinnedMunicipalities,
    focusId,
    boundaryLoading,
    hasBoundary: Boolean(activeBoundary),
    operationResult,
    focusedResultPointId,
    onFocusResultPoint: focusResultPoint,
    fleetGroupLabels,
    assetIssuesById: pinnedAssetIssuesById,
    focusedAssetImages: selectedAssetImages,
    assetImagesLoading,
    assetGalleryIdx,
    onAssetGalleryIdxChange: setAssetGalleryIdx,
    onFocusPinnedEntity: (id: string, category: EntityCategory) =>
      focusOmniSelection(id, category),
    onRemovePinnedEntity: removeOmniSelection,
    onClearOperation: clearActiveStepResult,
    steps: operationSteps,
    activeStepId,
    onSelectStep: handleSelectStep,
    onAddStep: handleAddStep,
    onRemoveStep: handleRemoveStep,
    onPickPriorPoint: pickPriorPointForActiveStep,
    mapRef,
    onShare: handleShareOperation,
    canShare: canShareOperation,
    shareCopied,
    onSelectTableEntity: selectTableEntity,
    inlineEntityOperations,
    myLocationPlace,
    onClearMyLocation: clearMyLocation,
    focusedPlace,
    onClearFocusedPlace: () => setFocusedResultPointId(null),
  }

  const defaultCenter: [number, number] = [39.8283, -98.5795]
  const defaultZoom = 4

  return (
    <div
      className={`relative flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-base-200 ${
        isDisplayKiosk
          ? 'rounded-none border-0 shadow-none'
          : 'rounded-lg border border-base-300 shadow'
      }`}
    >      <MapContainer
        ref={mapRef}
        center={defaultCenter}
        zoom={defaultZoom}
        zoomSnap={0.1}
        zoomDelta={0.1}
        className="absolute inset-0 z-0 h-full w-full outline-none"
        scrollWheelZoom
        attributionControl
        zoomControl={false}
      >
        <ControlCenterMapBasemapLayer mapStyleId={mapStyleId} />
        {weatherEnabled && weatherFrame ? (
          <RainViewerRadarLayer frame={weatherFrame} />
        ) : null}
        <InvalidateMapSize />
        <TrackUserCameraGestures />
        <StopMapOnSelectionChange selectedId={focusId} />
        <FlyToSelectedEntity
          entity={focusedMapEntity}
          enabled={useMarkerFlyTo}
          focusToken={entityFocusToken}
          focusAnchor={mapFocusAnchor}
          layoutFocusToken={panelOpenFocusToken}
        />
        <FitOperationResultBounds
          result={isDisplayKiosk ? null : operationResult}
          origin={operationOrigin}
          nearbyRadiusMiles={
            !isDisplayKiosk && operationResult?.operatorId === 'nearby'
              ? operationInputs.radiusMiles
              : null
          }
        />
        <FocusOperationResultPoint
          point={isDisplayKiosk ? null : focusedResultPoint}
          focusToken={resultFocusToken}
          focusAnchor={mapFocusAnchor}
          layoutFocusToken={panelOpenFocusToken}
        />
        {BOUNDARY_OVERLAY_LEVELS.map((level) => {
          const fc = boundaryOverlayGeojson[level]
          if (!fc || fc.features.length === 0) return null
          return (
            <MunicipalityBoundaryLayer
              key={`overlay-${level}`}
              geojson={fc}
              color={MUNICIPALITY_LEVEL_COLOR[level]}
              fitKey={null}
              maxZoom={boundaryFitMaxZoom(level)}
              weight={level === 'state' ? 2 : 1.5}
              fillOpacity={level === 'state' ? 0.08 : 0.12}
            />
          )
        })}
        <MunicipalityBoundaryLayer
          geojson={activeBoundary}
          color={
            municipalityForBoundary?.isExternal
              ? EXTERNAL_MUNICIPALITY_COLOR
              : municipalityForBoundary
                ? MUNICIPALITY_LEVEL_COLOR[municipalityForBoundary.level]
                : MUNICIPALITY_LEVEL_COLOR.city
          }
          fitKey={
            !isDisplayKiosk &&
            activeBoundary &&
            municipalityForBoundary &&
            focusingMunicipality
              ? `${municipalityForBoundary.id}:${entityFocusToken}`
              : null
          }
          maxZoom={
            municipalityForBoundary ? boundaryFitMaxZoom(municipalityForBoundary.level) : 12
          }
        />
        {operationResult?.routeLine ? (
          <Polyline
            positions={operationResult.routeLine}
            pathOptions={{ color: '#7c3aed', weight: 3, opacity: 0.75, dashArray: '8 6' }}
          />
        ) : null}
        {operationResult?.operatorId === 'nearby' &&
        operationOrigin &&
        Number.isFinite(operationOrigin.lat) &&
        Number.isFinite(operationOrigin.lng) &&
        operationInputs.radiusMiles > 0 ? (
          <Circle
            center={[operationOrigin.lat, operationOrigin.lng]}
            radius={operationInputs.radiusMiles * 1609.344}
            pathOptions={{
              color: '#2563eb',
              weight: 2,
              opacity: 0.55,
              fillColor: '#2563eb',
              fillOpacity: 0.08,
              dashArray: '6 6',
            }}
          />
        ) : null}
        {selectedPerson &&
        !personHasMapPin(selectedPerson) &&
        focusId === selectedPerson.id &&
        !isMobile ? (
          <UnlocatedEntityDockedPopup
            person={selectedPerson}
            open
            openPopupToken={entityFocusToken}
            operationRunning={operationRunning}
            operationResultOperatorId={operationResult?.operatorId ?? null}
            destinationOptions={popupDestinationOptions.filter(
              (opt) => opt.id !== entityRef('person', selectedPerson.id),
            )}
            preferredDestinationRef={pickOmniDestinationRef(selectedPerson.id)}
            onRunOperator={runOmniOperatorFromPopup}
          />
        ) : null}
        <MapClusterLayer
          markers={clusterableMarkers}
          excludeKeys={clusterExcludeKeys}
          viewport={{ isMobile, outputOpen }}
          onSelectMember={selectClusterMember}
        >
          {(layout) => (
            <>
              {mapEntitiesForDisplay.map((entity) => {
                const markerKey = `entity:${entity.category}:${entity.id}`
                const resultFocused =
                  resultEntityFocus?.id === entity.id &&
                  resultEntityFocus.category === entity.category
                const selected = focusId === entity.id || resultFocused
                // Coincident piles stay clustered, but still mount the selected pin
                // so the normal entity popup / ops menu works.
                const showOverCoincidentCluster =
                  selected && hasCoincidentNeighbor(clusterableMarkers, markerKey)
                if (!layout.unclusteredKeys.has(markerKey) && !showOverCoincidentCluster) {
                  return null
                }
                return (
                  <EntityMapMarkerModule
                    key={`${entity.category}:${entity.id}`}
                    entity={entity}
                    selected={selected}
                    openPopupToken={focusId === entity.id ? entityFocusToken : 0}
                    onSelect={selectEntityFromMapForChain}
                    operators={operatorsForEntityId.get(entity.id) ?? []}
                    onRunOperator={runOmniOperatorFromPopup}
                    onInputsEdit={handlePopupOperatorInputsEdit}
                    operationRunning={operationRunning}
                    destinationOptions={popupDestinationOptions.filter(
                      (opt) =>
                        opt.id !== entityRef(entity.category as OmniCategory, entity.id),
                    )}
                    preferredDestinationRef={pickOmniDestinationRef(entity.id)}
                    operationResultOperatorId={
                      focusId === entity.id && isLookAroundRootEntity(entity)
                        ? lookAroundDockOperatorId
                        : null
                    }
                    suppressPopup={isMobile}
                    hidePin={showOverCoincidentCluster}
                  />
                )
              })}
              {syntheticResultMapPoints.map(({ point, fromStepId }) => {
                const markerKey = `result:${fromStepId}:${point.id}`
                const focused = focusedResultPointId === point.id
                const showOverCoincidentCluster =
                  focused && hasCoincidentNeighbor(clusterableMarkers, markerKey)
                if (!layout.unclusteredKeys.has(markerKey) && !showOverCoincidentCluster) {
                  return null
                }
                return (
                  <OperationResultMarkerModule
                    key={`op-${point.id}`}
                    point={point}
                    fromStepId={fromStepId}
                    iconDef={resultMarkerIcon(point)}
                    focused={focused}
                    onFocus={focusResultPoint}
                    operators={placeOperators}
                    onRunOperator={runOperatorFromResultMapPoint}
                    onInputsEdit={handlePopupOperatorInputsEdit}
                    operationRunning={operationRunning}
                    destinationOptions={popupDestinationOptions}
                    preferredDestinationRef=""
                    operationResultOperatorId={
                      focused && activeStepOriginPoint?.point.id === point.id
                        ? lookAroundDockOperatorId
                        : null
                    }
                    suppressPopup={isMobile}
                    hidePin={showOverCoincidentCluster}
                  />
                )
              })}
              {userLocationPoint ? (
                <OperationResultMarkerModule
                  key={`op-${userLocationPoint.id}`}
                  point={userLocationPoint}
                  fromStepId=""
                  iconDef={resultMarkerIcon(userLocationPoint)}
                  focused={focusedResultPointId === userLocationPoint.id}
                  onFocus={focusResultPoint}
                  operators={placeOperators}
                  onRunOperator={runOperatorFromResultMapPoint}
                  onInputsEdit={handlePopupOperatorInputsEdit}
                  operationRunning={operationRunning}
                  destinationOptions={popupDestinationOptions.filter(
                    (opt) =>
                      opt.id !== `${userLocationPoint.lat},${userLocationPoint.lng}`,
                  )}
                  preferredDestinationRef=""
                  operationResultOperatorId={
                    focusedResultPointId === userLocationPoint.id &&
                    activeStepOriginPoint?.point.id === userLocationPoint.id
                      ? lookAroundDockOperatorId
                      : null
                  }
                  suppressPopup={isMobile}
                />
              ) : null}
            </>
          )}
        </MapClusterLayer>
      </MapContainer>

      {!omniBootstrapComplete ? (
        <MapLoadingOverlay
          jobsLoading={jobsLoading}
          peopleLoading={peopleLoading}
          assetsLoading={assetsLoading}
          municipalitiesLoading={municipalitiesLoading}
          offsetForToolbar={!isDisplayKiosk}
        />
      ) : null}

      {!isDisplayKiosk ? <EntityDragGhost /> : null}

      {!isDisplayKiosk ? (
      <ControlCenterMapToolbar
        mapRef={mapRef}
        mapStyleId={mapStyleId}
        setMapStyleId={setMapStyleId}
        inlineMapTools={inlineMapTools}
        reserveEndSpace={isDesktop && outputOpen}
        omniQuery={omniQuery}
        onOmniQueryChange={setOmniQuery}
        omniHits={omniHits}
        omniBootstrapping={!omniBootstrapComplete}
        omniSearching={omniCensusSearching || Boolean(censusPlaceResolvingId)}
        omniOpen={omniOpen}
        onOmniOpenChange={setOmniOpen}
        onSelectOmniHit={selectOmniHit}
        omniBadges={omniBadges}
        omniFocusId={focusId}
        onFocusOmniSelection={focusOmniSelection}
        onRemoveOmniSelection={removeOmniSelection}
        onClearOmniSelections={clearOmniSelections}
        filtersOpen={omniFiltersOpen}
        onFiltersOpenChange={handleFiltersOpenChange}
        activeFilterTypes={omniActiveFilterTypes}
        onToggleFilterType={toggleOmniFilterType}
        jobFilters={jobFilters}
        personFilters={personFilters}
        assetFilters={assetFilters}
        municipalityFilters={municipalityFilters}
        onJobFiltersChange={setJobFilters}
        onPersonFiltersChange={setPersonFilters}
        onAssetFiltersChange={setAssetFilters}
        onMunicipalityFiltersChange={setMunicipalityFilters}
        jobStates={omniJobStates}
        jobProjectTypeOptions={omniJobProjectTypeOptions}
        jobStatusOptions={omniJobStatusOptions}
        assetStatusOptions={omniAssetStatusOptions}
        municipalityStates={omniMunicipalityStates}
        showAllJobs={mapMarkerVisibility.jobs}
        showAllPeople={mapMarkerVisibility.people}
        showAllAssets={mapMarkerVisibility.assets}
        onShowAllJobsChange={(next) =>
          setMapMarkerVisibility((prev) => ({ ...prev, jobs: next }))
        }
        onShowAllPeopleChange={(next) =>
          setMapMarkerVisibility((prev) => ({ ...prev, people: next }))
        }
        onShowAllAssetsChange={(next) =>
          setMapMarkerVisibility((prev) => ({ ...prev, assets: next }))
        }
        boundaryShowLevel={showLevelFromBoundaryOverlays(boundaryOverlays)}
        onBoundaryShowLevelChange={(next) =>
          setBoundaryOverlays(boundaryOverlaysFromShowLevel(next))
        }
        boundaryOverlaysLoading={boundaryOverlaysLoading}
        weatherEnabled={weatherEnabled}
        onWeatherEnabledChange={setWeatherEnabled}
        onUseMyLocation={useMyLocation}
        locatingMe={locatingMe}
        myLocationActive={Boolean(userLocationPoint)}
        locateError={locateError}
        onClearMyLocation={clearMyLocation}
        onFocusMyLocation={() => focusResultPoint(USER_LOCATION_POINT_ID)}
      />
      ) : null}

      {isDesktop ? (
      <div
        className={`pointer-events-none absolute top-3 right-3 z-10 flex min-h-0 max-h-[calc(100%-1.5rem)] max-w-[min(36rem,46%)] flex-row items-start transition-[gap] duration-300 ease-out ${
          outputOpen ? 'gap-2' : 'gap-0'
        }`}
      >
        {!isDisplayKiosk ? (
          <PanelToggleChip
            open={outputOpen}
            onToggle={() => setOutputOpen((v) => !v)}
            openIcon={faChevronRight}
            closedIcon={faChevronLeft}
            openLabel="Hide output panel"
            closedLabel="Show output panel"
            className="shrink-0"
            badgeCount={collapsedOutputBadgeCount}
            flashToken={outputBadgeFlashToken}
          />
        ) : null}
        <div
          className={`grid min-h-0 min-w-0 flex-1 transition-[grid-template-columns] duration-300 ease-out ${
            outputOpen ? 'grid-cols-[minmax(0,1fr)]' : 'grid-cols-[0fr]'
          }`}
        >
          <div className="min-w-0 overflow-hidden">
            <div
              className={`flex min-h-0 max-h-[calc(100vh-6.5rem)] w-full min-w-[min(20rem,100%)] max-w-full flex-col transition-[opacity,transform] duration-300 ease-out ${
                outputOpen
                  ? 'translate-x-0 opacity-100'
                  : 'pointer-events-none translate-x-2 opacity-0'
              }`}
            >
              <OutputPanel {...outputPanelProps} />
            </div>
          </div>
        </div>
      </div>
      ) : (
      <div className="pointer-events-none absolute inset-3 z-10 flex flex-col-reverse items-stretch gap-2">
        {outputOpen ? (
          <div className="pointer-events-none flex h-1/2 w-full min-h-0">
            <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
              <OutputPanel {...outputPanelProps} />
            </div>
          </div>
        ) : null}
        <div className="pointer-events-none flex items-center justify-center gap-2">
          <PanelToggleChip
            open={outputOpen}
            onToggle={() => setOutputOpen((v) => !v)}
            openIcon={faChevronDown}
            closedIcon={faChevronUp}
            openLabel="Hide output panel"
            closedLabel="Show output panel"
            badgeCount={collapsedOutputBadgeCount}
            flashToken={outputBadgeFlashToken}
          />
        </div>
      </div>
      )}
    </div>
  )
}

export default ControlCenterMap
